// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { createPortal } from 'react-dom'
import * as api from '../../api/client'
import SearchableSelect from '../../components/common/SearchableSelect'
import type { RxViewCard, RxViewField, PioneerPath } from '../../api/client'
import { ChevronUp, ChevronDown, Plus, Trash2, Eye, EyeOff, GripVertical } from 'lucide-react'
import { usePointerReorder, reorderByKey } from '../../components/usePointerReorder'
import ViewManagerPanel from '../../components/ViewManagerPanel'
import MappingBackfillPanel from '../../components/admin/MappingBackfillPanel'
import { useStartMappingBackfill } from '../../hooks/queries/mappingBackfill'

/**
 * Prescription View Builder — SaaS admin, per-pharmacy. Composes the ordered
 * cards & fields that drive the Rx detail view, each field carrying its
 * PioneerRx mapping (or left custom). RBAC is applied at render, not here:
 * a field/card a role can't see is dropped, and custom fields are created
 * deny-by-default (hidden until a role rule grants them).
 */

// Faithful mirror of the actual Rx detail view (RxDetailPage). Used to pre-fill the
// builder for a pharmacy that has NO saved layout yet, so the preview shows exactly
// what that pharmacy currently sees — same cards, fields, labels, and the same columns
// the view reads (display_name, primary_phone, *_employee_id, etc.), not mapping-table
// columns. Computed columns (quantity_authorized, refills_authorized, sig, address) are
// rendered combined by RxDetailPage's layoutFieldValue to match the view exactly.
type DefField = { table: string; col: string; label: string }
type DefCard = { key: string; title: string; kind: RxViewCard['kind']; fields?: DefField[]; module?: string }
const RX_DEFAULT: DefCard[] = [
  { key: 'drug', title: 'Drug & directions', kind: 'fields', fields: [
    { table: 'rx_items', col: 'drug_name', label: 'Prescribed' },
    { table: 'rx_items', col: 'drug_strength', label: 'Strength' },
    { table: 'rx_items', col: 'drug_form', label: 'Form' },
    { table: 'rx_items', col: 'sig', label: 'Sig' },
    { table: 'rx_items', col: 'quantity_authorized', label: 'Quantity' },
    { table: 'rx_items', col: 'days_supply', label: 'Days supply' },
    { table: 'rx_items', col: 'refills_authorized', label: 'Refills' },
  ] },
  { key: 'dispensed', title: 'Prescribed → dispensed', kind: 'fields', fields: [
    { table: 'rx_items', col: 'prescribed_ndc', label: 'Prescribed NDC' },
    { table: 'rx_items', col: 'prescribed_gcn', label: 'Prescribed GCN' },
    { table: 'rx_items', col: 'prescribed_brand_name', label: 'Prescribed brand' },
    { table: 'rx_items', col: 'prescribed_generic_name', label: 'Prescribed generic' },
  ] },
  { key: 'patient', title: 'Patient', kind: 'fields', fields: [
    { table: 'patients', col: 'display_name', label: 'Name' },
    { table: 'patients', col: 'date_of_birth', label: 'Date of birth' },
    { table: 'patients', col: 'gender', label: 'Gender' },
    { table: 'patients', col: 'primary_phone', label: 'Phone' },
    { table: 'patients', col: 'email', label: 'Email' },
    { table: 'patients', col: 'address', label: 'Address' },
    { table: 'patients', col: 'allergies', label: 'Allergies' },
    { table: 'patients', col: 'language_text', label: 'Language' },
    { table: 'patients', col: 'delivery_method', label: 'Delivery preference' },
  ] },
  { key: 'prescriber', title: 'Prescriber', kind: 'fields', fields: [
    { table: 'prescribers', col: 'display_name', label: 'Name' },
    { table: 'prescribers', col: 'npi', label: 'NPI' },
    { table: 'prescribers', col: 'dea_number', label: 'DEA' },
    { table: 'prescribers', col: 'primary_phone', label: 'Phone' },
    { table: 'prescribers', col: 'primary_fax', label: 'Fax' },
    { table: 'prescribers', col: 'email', label: 'Email' },
    { table: 'prescribers', col: 'address', label: 'Address' },
  ] },
  { key: 'workbench', title: 'WorkBench / Operations', kind: 'workbench' },
  { key: 'pioneerrx', title: 'PioneerRx status (verbatim)', kind: 'fields', fields: [
    { table: 'rx_items', col: 'pioneerrx_rx_status', label: 'PMS Rx status' },
    { table: 'rx_items', col: 'pioneerrx_tx_status', label: 'PMS Tx status' },
    { table: 'rx_items', col: 'origin_type', label: 'Origin' },
    { table: 'rx_items', col: 'date_written', label: 'Date written' },
    { table: 'rx_items', col: 'date_entered', label: 'Date entered' },
    { table: 'rx_items', col: 'date_filled_utc', label: 'Date filled' },
    { table: 'rx_items', col: 'expiration_date_utc', label: 'Expiration' },
    { table: 'rx_items', col: 'promise_time_utc', label: 'Promise time' },
    { table: 'rx_items', col: 'fill_request_method', label: 'Fill request' },
    { table: 'rx_items', col: 'drug_use_type', label: 'Drug use' },
  ] },
  { key: 'financials', title: 'Financials', kind: 'financials', fields: [
    { table: 'rx_items', col: 'ingredient_cost_submitted', label: 'Ingredient cost (submitted)' },
    { table: 'rx_items', col: 'ingredient_cost_paid', label: 'Ingredient cost (paid)' },
    { table: 'rx_items', col: 'dispensing_fee_submitted', label: 'Dispensing fee (submitted)' },
    { table: 'rx_items', col: 'dispensing_fee_paid', label: 'Dispensing fee (paid)' },
    { table: 'rx_items', col: 'total_price_submitted', label: 'Total price (submitted)' },
    { table: 'rx_items', col: 'total_price_paid', label: 'Total price (paid)' },
    { table: 'rx_items', col: 'patient_paid_amount', label: 'Patient paid' },
    { table: 'rx_items', col: 'usual_customary_price', label: 'Usual & customary' },
    { table: 'rx_items', col: 'acquisition_cost', label: 'Acquisition cost' },
  ] },
  { key: 'team', title: 'Pharmacy team', kind: 'fields', fields: [
    { table: 'rx_items', col: 'data_entry_employee_id', label: 'Data entry' },
    { table: 'rx_items', col: 'pharmacist_employee_id', label: 'Pharmacist' },
    { table: 'rx_items', col: 'checked_by_pharmacist_employee_id', label: 'Checked by' },
    { table: 'rx_items', col: 'filled_by_employee_id', label: 'Filled by' },
    { table: 'rx_items', col: 'sale_completed_by_employee_id', label: 'Sale completed by' },
  ] },
  { key: 'comments', title: 'Comments & alerts', kind: 'comments' },
  // Sections promoted from the hardcoded Rx detail render (198): component-driven, so show/hide,
  // reorder, role-gate and (clinical_comments) rename / informational-only only — no field rows.
  { key: 'delivery', title: 'Delivery', kind: 'delivery' },
  { key: 'claims', title: 'Claims', kind: 'claims' },
  { key: 'fills', title: 'Fills', kind: 'fills' },
  { key: 'clinical_comments', title: 'Clinical comments', kind: 'clinical_comments' },
  { key: 'timeline', title: 'Timeline', kind: 'timeline' },
  // D-DRUGREF-3: DrugReference fields (joined per Rx by NDC). Gated to the drugreference module;
  // filtered out of the builder unless the selected pharmacy is entitled. Values resolve via a
  // separate module-gated fetch on the detail page (core Rx query untouched).
  { key: 'drugreference', title: 'DrugReference', kind: 'fields', module: 'drugreference', fields: [
    { table: 'drug_reference', col: 'category', label: 'Drug category' },
    { table: 'drug_reference', col: 'rems_program', label: 'REMS program' },
    { table: 'drug_reference', col: 'days_before_procedure', label: 'Days before procedure' },
    { table: 'drug_reference', col: 'copay_program', label: 'Copay program' },
  ] },
  // D-SOC-2: where this Rx is delivered. Values resolve from GET /rx/{id}/site-of-care on the page.
  { key: 'site_of_care', title: 'Site of care', kind: 'fields', fields: [
    { table: 'rx_site_of_care', col: 'facility_type', label: 'Site of care' },
    { table: 'rx_site_of_care', col: 'facility_name', label: 'Facility' },
    { table: 'rx_site_of_care', col: 'facility_phone', label: 'Facility phone' },
    { table: 'rx_site_of_care', col: 'match_method', label: 'How matched' },
    // D-SOC-3: facility contact + operations
    { table: 'rx_site_of_care', col: 'contact_name', label: 'Contact name' },
    { table: 'rx_site_of_care', col: 'contact_phone', label: 'Contact phone' },
    { table: 'rx_site_of_care', col: 'contact_email', label: 'Contact email' },
    { table: 'rx_site_of_care', col: 'operating_hours', label: 'Operating hours' },
    { table: 'rx_site_of_care', col: 'delivery_cutoff_time', label: 'Delivery cutoff time' },
  ] },
]

// Patient view default — mirrors PatientDetailPage (Demographics / Contact / Clinical).
const PATIENT_DEFAULT: DefCard[] = [
  { key: 'demographics', title: 'Demographics', kind: 'fields', fields: [
    { table: 'patients', col: 'display_name', label: 'Name' },
    { table: 'patients', col: 'first_name', label: 'First name' },
    { table: 'patients', col: 'last_name', label: 'Last name' },
    { table: 'patients', col: 'date_of_birth', label: 'Date of birth' },
    { table: 'patients', col: 'gender', label: 'Gender' },
    { table: 'patients', col: 'height_inches', label: 'Height' },
    { table: 'patients', col: 'weight_ounces', label: 'Weight' },
  ] },
  { key: 'contact', title: 'Contact & preferences', kind: 'fields', fields: [
    { table: 'patients', col: 'primary_phone', label: 'Phone' },
    { table: 'patients', col: 'email', label: 'Email' },
    { table: 'patients', col: 'address', label: 'Address' },
    { table: 'patients', col: 'language_text', label: 'Language' },
    { table: 'patients', col: 'delivery_method', label: 'Delivery' },
    { table: 'patients', col: 'autofill_mode', label: 'Autofill' },
  ] },
  { key: 'clinical', title: 'Clinical', kind: 'fields', fields: [
    { table: 'patients', col: 'allergies', label: 'Allergies' },
    { table: 'patients', col: 'allergy_alert_type', label: 'Allergy alert' },
    { table: 'patients', col: 'insurance_carrier', label: 'Insurance' },
    { table: 'patients', col: 'notes', label: 'Notes' },
    { table: 'patients', col: 'patient_risk_score', label: 'Risk score' },
  ] },
  // D-SOC-2: the patient's current site of care (GET /patients/{id}/site-of-care).
  { key: 'site_of_care', title: 'Site of care', kind: 'fields', fields: [
    { table: 'patient_site_of_care', col: 'facility_type', label: 'Site of care' },
    { table: 'patient_site_of_care', col: 'facility_name', label: 'Facility' },
    { table: 'patient_site_of_care', col: 'facility_phone', label: 'Facility phone' },
    { table: 'patient_site_of_care', col: 'contact_name', label: 'Contact name' },
    { table: 'patient_site_of_care', col: 'contact_phone', label: 'Contact phone' },
    { table: 'patient_site_of_care', col: 'contact_email', label: 'Contact email' },
    { table: 'patient_site_of_care', col: 'operating_hours', label: 'Operating hours' },
    { table: 'patient_site_of_care', col: 'delivery_cutoff_time', label: 'Delivery cutoff time' },
  ] },
]

// Prescriber view default — mirrors PrescriberDetailPage (Credentials / Practice / Contact).
const PRESCRIBER_DEFAULT: DefCard[] = [
  { key: 'credentials', title: 'Credentials', kind: 'fields', fields: [
    { table: 'prescribers', col: 'npi', label: 'NPI' },
    { table: 'prescribers', col: 'dea_number', label: 'DEA' },
    { table: 'prescribers', col: 'state_license', label: 'State license' },
    { table: 'prescribers', col: 'prescriber_type', label: 'Type' },
    { table: 'prescribers', col: 'specialty', label: 'Specialty' },
    { table: 'prescribers', col: 'status', label: 'Status' },
  ] },
  { key: 'practice', title: 'Practice', kind: 'fields', fields: [
    { table: 'prescribers', col: 'clinic_name', label: 'Clinic' },
    { table: 'prescribers', col: 'address', label: 'Address' },
  ] },
  { key: 'contact', title: 'Contact', kind: 'fields', fields: [
    { table: 'prescribers', col: 'primary_phone', label: 'Phone' },
    { table: 'prescribers', col: 'primary_fax', label: 'Fax' },
    { table: 'prescribers', col: 'email', label: 'Email' },
  ] },
  // D-SOC-2: a prescriber's site of care is an aggregate of their patients' (GET /prescribers/{id}/site-of-care).
  { key: 'site_of_care', title: 'Site of care', kind: 'fields', fields: [
    { table: 'prescriber_site_of_care', col: 'facilities_served', label: 'Facilities served' },
    { table: 'prescriber_site_of_care', col: 'facility_patient_count', label: 'Patients at facilities' },
    { table: 'prescriber_site_of_care', col: 'residential_patient_count', label: 'Patients at home' },
  ] },
]

// ASP-32 / D-HOME-1: OwnerHome sections as builder-managed cards (show/hide/reorder only —
// component-driven, no field rows). No saved layout => the home page renders its built-in
// default exactly as before, so unconfigured pharmacies are untouched.
// MUST mirror OwnerHome.tsx DEFAULT_HOME_ORDER exactly (the builder's claim about what an
// unconfigured pharmacy renders). 'action_aging' is OPT-IN (ASP-07) and deliberately absent —
// add it via the "Add section" picker. Reconciled in D-HOME-2 part A.
const HOME_DEFAULT: DefCard[] = [
  { key: 'status_pulse',      title: 'Prescription status (pulse tiles)', kind: 'home_section' },
  { key: 'action_comments',   title: 'Provider comments',                 kind: 'home_section' },
  { key: 'action_prior_auth', title: 'Prior auth',                        kind: 'home_section' },
  { key: 'action_holds',      title: 'Holds over 24h',                    kind: 'home_section' },
  { key: 'action_rejected',   title: 'Rejected (24h)',                    kind: 'home_section' },
  { key: 'volume',            title: 'Volume — last 14 days',             kind: 'home_section' },
  { key: 'go_deeper',         title: 'Go deeper (links)',                 kind: 'home_section' },
]
// Every home section the page knows how to render (OwnerHome sectionRenderers +
// actionCardRenderers), whether or not it's in the default composition. Drives the
// "Add section" picker (re-add a removed section / enable opt-in ones like aging) and
// the honest per-section preview. `metric` documents what the number IS today — fixed
// server-side SQL in GetOwnerSummary; per-card metric/aggregation config is D-HOME-2 part B.
const HOME_SECTION_CATALOG: { key: string; title: string; metric: string; body: string; optIn?: boolean }[] = [
  { key: 'status_pulse',      title: 'Prescription status (pulse tiles)', metric: 'One tile per PioneerRx status present in this pharmacy\u2019s data \u2014 COUNT of Rx per raw pioneerrx_rx_status', body: 'Tiles are data-driven (e.g. Fill In Progress, On Hold, Refills Remaining, Discontinued, Expired or No Refills, Fill Requested, Transferred). Individual tiles are not yet configurable here.' },
  { key: 'action_comments',   title: 'Provider comments',                 metric: 'COUNT of unread provider comments', body: 'List of the 5 most recent unread comments \u2192 links to the Rx.' },
  { key: 'action_prior_auth', title: 'Prior auth',                        metric: 'COUNT of Rx awaiting prior authorization', body: 'Summary line \u2192 clinical dashboard.' },
  { key: 'action_holds',      title: 'Holds over 24h',                    metric: 'COUNT of Rx on hold longer than 24 hours', body: 'Worklist of the 5 oldest holds with hours-in-hold \u2192 links to the Rx.' },
  { key: 'action_rejected',   title: 'Rejected (24h)',                    metric: 'COUNT of rejections in the last 24 hours', body: 'Summary line \u2192 operational dashboard.' },
  { key: 'action_aging',      title: 'Aging \u2014 pre-check over 10 days',    metric: 'COUNT of Rx unverified in pre-check for over 10 days', body: 'Worklist of the 5 oldest with days-in-pre-check \u2192 links to the Rx.', optIn: true },
  { key: 'volume',            title: 'Volume \u2014 last 14 days',             metric: 'Rx received today + AVG per day over 14 days', body: '14-day sparkline of daily Rx volume.' },
  { key: 'go_deeper',         title: 'Go deeper (links)',                 metric: 'No metric \u2014 navigation links', body: 'Operational dashboard \u00b7 Clinical dashboard \u00b7 Reports \u00b7 Patients.' },
]
// D-HOME-2 part B — client mirror of the server's HomeMetricCatalog (functions/Workflow/
// HomeMetricFunctions.cs). The SERVER allowlist is the security boundary; this mirror only
// drives the editor UI. Keep in sync when extending the catalog.
const HOME_METRIC_CATALOG = {
  sources: {
    rx_items: {
      label: 'Prescriptions (rx_items)',
      filterFields: {
        state: { label: 'Workflow state', kind: 'text' },
        payer_state: { label: 'Payer state', kind: 'text' },
        pioneerrx_rx_status: { label: 'PioneerRx status', kind: 'text' },
        drug_name: { label: 'Drug name', kind: 'text' },
        refills_remaining: { label: 'Refills remaining', kind: 'number' },
        days_supply: { label: 'Days supply', kind: 'number' },
        quantity_authorized: { label: 'Quantity authorized', kind: 'number' },
      } as Record<string, { label: string; kind: 'text' | 'number' }>,
      measures: ['refills_remaining', 'days_supply', 'quantity_authorized'],
      dateCols: ['created_at', 'last_state_change_at', 'date_filled_utc'],
      groupBy: ['state', 'payer_state', 'pioneerrx_rx_status'],
    },
  },
  aggs: ['count', 'sum', 'avg', 'min', 'max'],
  textOps: ['eq', 'ne', 'in', 'is_null', 'not_null'],
  numberOps: ['eq', 'ne', 'in', 'gt', 'gte', 'lt', 'lte', 'is_null', 'not_null'],
}
const OP_LABELS: Record<string, string> = { eq: '=', ne: '≠', in: 'in list', gt: '>', gte: '≥', lt: '<', lte: '≤', is_null: 'is empty', not_null: 'is set' }
// Common pulse statuses seen in PioneerRx data — a starting list for the tile editor; tiles
// themselves stay data-driven, admins can also type any raw status.
const KNOWN_PULSE_STATUSES = ['Fill In Progress', 'On Hold', 'Refills Remaining', 'Discontinued', 'Expired or No Refills', 'Fill Requested', 'Transferred']
function metricSummary(cfg: Record<string, any> | null | undefined): string {
  const m = cfg?.metric
  if (!m) return 'No metric configured yet — pick an aggregation and filters below.'
  const src = HOME_METRIC_CATALOG.sources[m.source as 'rx_items']
  const agg = (m.agg || 'count').toUpperCase()
  const meas = m.agg && m.agg !== 'count' ? ` of ${src?.filterFields[m.measure]?.label ?? m.measure ?? '?'}` : ''
  const fl = (m.filters ?? []).map((f: any) => `${src?.filterFields[f.field]?.label ?? f.field} ${OP_LABELS[f.op] ?? f.op} ${f.op === 'is_null' || f.op === 'not_null' ? '' : (f.values ?? [f.value]).join(', ')}`).join(' AND ')
  const win = m.window ? `, ${m.window.mode === 'older_than' ? 'older than' : 'within last'} ${m.window.amount ?? m.window.last_days} ${m.window.unit ?? 'days'} (${m.window.field})` : ''
  const grp = m.group_by ? `, one tile per ${src?.filterFields[m.group_by]?.label ?? m.group_by}` : ''
  return `${agg}${meas} of ${src?.label ?? m.source}${fl ? ` where ${fl}` : ''}${win}${grp}`
}
const DEFAULT_LAYOUTS: Record<string, DefCard[]> = { rx: RX_DEFAULT, patient: PATIENT_DEFAULT, prescriber: PRESCRIBER_DEFAULT, home: HOME_DEFAULT }
const SURFACE_TITLES: Record<string, string> = { rx: 'Prescription view builder', patient: 'Patient view builder', prescriber: 'Prescriber view builder', home: 'Home dashboard builder', facility: 'Facility view builder' }

// Identity-band fields for each surface — these mirror the columns guarded on the
// detail pages (showCore/showOpt). The header-band editor toggles these in place;
// `optional` fields are off by default (added on request, e.g. Address). The name/
// title row is always shown and is intentionally not listed (not removable).
type BandFieldDef = { col: string; label: string; table: string; optional?: boolean }
const BAND_FIELDS: Record<string, BandFieldDef[]> = {
  patient: [
    { col: 'status', label: 'Status', table: 'patients' },
    { col: 'date_of_birth', label: 'DOB', table: 'patients' },
    { col: 'gender', label: 'Gender', table: 'patients' },
    { col: 'primary_phone', label: 'Phone', table: 'patients' },
    { col: 'allergies', label: 'Allergies', table: 'patients' },
    { col: 'address', label: 'Address', table: 'patients', optional: true },
  ],
  rx: [
    { col: 'rx_number', label: 'Rx #', table: 'rx_items' },
    { col: 'refill_number', label: 'Refill', table: 'rx_items' },
    { col: 'prescriber', label: 'Prescriber', table: 'prescribers' },
  ],
  prescriber: [
    { col: 'suffix', label: 'Suffix', table: 'prescribers' },
    { col: 'status', label: 'Status', table: 'prescribers' },
    { col: 'npi', label: 'NPI', table: 'prescribers' },
    { col: 'dea_number', label: 'DEA', table: 'prescribers' },
    { col: 'specialty', label: 'Specialty', table: 'prescribers' },
  ],
}
const BAND_NAME_LABEL: Record<string, string> = { patient: 'Patient name', rx: 'Patient name', prescriber: 'Prescriber name' }

// Demo values shown in the live preview so masking/hiding is visible per role
// (e.g. financials). Keyed by owlrx_table.owlrx_column; generic fallback below.
const SAMPLE: Record<string, string> = {
  'rx_items.drug_name': 'Lisinopril', 'rx_items.drug_strength': '10mg', 'rx_items.drug_form': 'tablet',
  'rx_items.sig': 'Take 1 tablet by mouth daily', 'rx_items.quantity_authorized': '30', 'rx_items.quantity_unit': 'tablets',
  'rx_items.days_supply': '30', 'rx_items.refills_authorized': '5', 'rx_items.refills_remaining': '5',
  'rx_items.prescribed_ndc': '00071-0155-23', 'rx_items.prescribed_brand_name': 'Prinivil', 'rx_items.prescribed_generic_name': 'Lisinopril',
  'patients.first_name': 'Demo', 'patients.last_name': 'Patient', 'patients.date_of_birth': '1990-01-01',
  'patients.sex': 'Female', 'patients.email': 'patient.demo@owlconsultations.com', 'rx_items.pioneerrx_rx_status': 'Filled', 'rx_items.current_tx_status_text': 'Complete',
  'rx_items.date_written': '2026-06-01', 'rx_items.date_filled_utc': '2026-06-02', 'rx_items.date_entered': '2026-06-01',
  'rx_items.expiration_date_utc': '2027-06-01',
  'rx_items.patient_paid_amount': '$10.00', 'rx_items.total_price_paid': '$42.10', 'rx_items.total_price_submitted': '$58.00',
  'rx_items.ingredient_cost_paid': '$38.10', 'rx_items.ingredient_cost_submitted': '$52.00',
  'rx_items.dispensing_fee_paid': '$4.00', 'rx_items.dispensing_fee_submitted': '$6.00',
  'rx_items.usual_customary_price': '$60.00', 'rx_items.acquisition_cost': '$30.00', 'rx_items.insurance_carrier': 'BlueCross PPO',
  'rx_items.pharmacist_pioneerrx_id': 'A. Rivera, PharmD', 'rx_items.filled_by_pioneerrx_id': 'J. Chen',
  'rx_items.data_entry_by_pioneerrx_id': 'M. Lopez', 'rx_items.checked_by_pharmacist_pioneerrx_id': 'A. Rivera, PharmD',
  'rx_items.sale_completed_by_employee_pioneerrx_id': 'K. Park',
  'rx_items.rx_comment_critical': 'Patient approved SMS and Autofill',
  'patients.display_name': 'Demo Patient', 'patients.gender': 'Female',
  'patients.primary_phone': '(555) 000-0000', 'patients.address': '789 Sample Avenue, Testville, CA 90000',
  'patients.allergies': 'No Known Allergies', 'patients.language_text': 'English', 'patients.delivery_method': 'Pickup',
  'prescribers.display_name': 'Dr. Alex Rivera', 'prescribers.npi': '1234567890', 'prescribers.dea_number': 'AR1234567',
  'prescribers.primary_phone': '(555) 111-2222', 'prescribers.primary_fax': '(555) 111-2223',
  'prescribers.email': 'arivera@example.com', 'prescribers.address': '12 Clinic Way, Testville, CA 90000',
  'rx_items.pioneerrx_tx_status': 'Complete', 'rx_items.origin_type': 'Electronic',
  'rx_items.promise_time_utc': '2026-06-02 14:00', 'rx_items.fill_request_method': 'Electronic', 'rx_items.drug_use_type': 'Maintenance',
  'rx_items.data_entry_employee_id': 'M. Lopez', 'rx_items.pharmacist_employee_id': 'A. Rivera, PharmD',
  'rx_items.checked_by_pharmacist_employee_id': 'A. Rivera, PharmD', 'rx_items.filled_by_employee_id': 'J. Chen',
  'rx_items.sale_completed_by_employee_id': 'K. Park',
  'patients.height_inches': '68″', 'patients.weight_ounces': '170.0 lb', 'patients.autofill_mode': 'Enabled',
  'patients.insurance_carrier': 'BlueCross PPO', 'patients.notes': 'Prefers afternoon delivery',
  'patients.patient_risk_score': '2', 'patients.allergy_alert_type': 'Verified',
  'prescribers.specialty': 'Internal Medicine',
  'prescribers.state_license': 'CA-A12345', 'prescribers.prescriber_type': 'MD',
  'prescribers.status': 'Active', 'prescribers.clinic_name': 'Testville Family Clinic',
}
function sampleFor(f: { owlrx_table: string; owlrx_column: string; owlrx_label: string }): string {
  const k = `${f.owlrx_table}.${f.owlrx_column}`
  if (SAMPLE[k]) return SAMPLE[k]
  const c = f.owlrx_column.toLowerCase()
  if (/(amount|paid|price|cost|fee|copay|charge|balance)/.test(c)) return '$42.10'
  if (/date|dob|expiration/.test(c)) return '2026-06-01'
  if (/phone|fax/.test(c)) return '(555) 000-0000'
  if (/email/.test(c)) return 'demo@example.com'
  if (/npi|dea|mrn|number|id/.test(c)) return '1234567890'
  if (/name/.test(c)) return 'Sample'
  return 'Sample value'
}
function maskSample(s: string): string {
  return s.startsWith('$') ? '$•••.••' : '•'.repeat(Math.min(Math.max(s.length, 4), 8))
}

export default function RxViewBuilderPage({ surface = 'rx' }: { surface?: string }) {
  const [pharmacies, setPharmacies] = useState<{ id: string; name: string }[]>([])
  const [pharmacyId, setPharmacyId] = useState<string>('')
  const [sp, setSp] = useSearchParams()
  // D-TABCOLS-1: per-tab column-override editor. ?listtab=<key> focuses the builder on ONE system
  // tab's columns (the 5 StationQueue-based tabs; insurance/refills are bespoke and excluded). Only
  // valid on the rx surface. When set, pharmacyList points at rx_list:<key> (seeded from shared
  // rx_list) and the page renders only the list-columns panel.
  const LIST_TAB_KEYS = ['intake', 'production', 'ready', 'hold', 'all'] as const
  const LIST_TAB_LABELS: Record<string, string> = { intake: 'Intake', production: 'Production', ready: 'Ready & out', hold: 'On hold', all: 'All prescriptions' }
  const rawListTab = sp.get('listtab')
  const listTab = (surface === 'rx' && rawListTab && (LIST_TAB_KEYS as readonly string[]).includes(rawListTab)) ? rawListTab : null
  const [cards, setCards] = useState<RxViewCard[]>([])
  const [paths, setPaths] = useState<PioneerPath[]>([])
  const [fieldDefaults, setFieldDefaults] = useState<Record<string, string>>({}) // table.column -> canonical PioneerRx path (DefaultMappings)
  // D-VIEWCOLS-3: companion list surfaces — one hook instance per audience panel.
  // Pharmacy home table = `${surface}_list`; provider portal (rx for now) =
  // `provider_rx_list`. Each holds one 'list_columns' card + per-column config
  // (provider_hidden / subtext_hidden) and self-heals against catalog + customs.
  const [pharmacyModules, setPharmacyModules] = useState<Set<string>>(new Set())
  const gateCat = (cat: { table: string; column: string; label: string; visible: boolean }[]) =>
    cat.filter(c => c.table !== 'drug_reference' || pharmacyModules.has('drugreference'))
  const customSources = useMemo(() => {
    const seen = new Set<string>()
    return cards.flatMap(c => (c.fields ?? []).filter((f: any) => f.owlrx_table === 'custom'))
      .filter((f: any) => !seen.has(f.owlrx_column) && !!seen.add(f.owlrx_column))
  }, [cards])
  const listCatalogGated = useMemo(() => gateCat(LIST_CATALOG[surface] ?? []), [surface, pharmacyModules])
  const pharmacyList = useListSurface(pharmacyId, surface === 'home' ? '' : (listTab ? `rx_list:${listTab}` : `${surface}_list`), listCatalogGated, cards, true, listTab ? 'rx_list' : undefined)
  const providerList = useListSurface(pharmacyId, surface === 'rx' ? 'provider_rx_list' : '', gateCat(PROVIDER_RX_CATALOG), cards, false)
  const patientRxList = useListSurface(pharmacyId, surface === 'patient' ? 'patient_rx_list' : '', gateCat(PATIENT_RX_CATALOG), cards, false)
  const providerPtRxList = useListSurface(pharmacyId, surface === 'patient' ? 'prov_pt_rx_list' : '', gateCat(PROVIDER_PT_RX_CATALOG), cards, false)
  const prescRxList = useListSurface(pharmacyId, surface === 'prescriber' ? 'presc_rx_list' : '', gateCat(PRESC_RX_CATALOG), cards, false)
  const facRxList = useListSurface(pharmacyId, surface === 'facility' ? 'fac_rx_list' : '', gateCat(FAC_RX_CATALOG), cards, false)
  const facPtList = useListSurface(pharmacyId, surface === 'facility' ? 'fac_pt_list' : '', gateCat(FAC_PT_CATALOG), cards, false)
  // fac_home is a CARD surface (its sections ARE the tiles), so it is edited with the same card
  // editor 'home' uses rather than a column panel.
  const missingFacHome = surface === 'facility' ? FAC_HOME_CATALOG.filter(s => !cards.some(c => c.card_key === s.key)) : []
  // ASP-38: any mapping change (detail-card repoint or list-column PioneerRx source) marks the
  // session dirty; Save then queues a backfill so existing prescriptions fill too.
  // D-MAP-1: the backfill runs on the SERVER (MappingBackfillPanel). It used to be a loop in this
  // tab, which stopped when the tab closed and, above 50,000 Rx, never started after Save: on ASP,
  // mappings added after July held values for 4% to 72% of prescriptions.
  const mappingsDirty = useRef(false)
  const startBackfill = useStartMappingBackfill(pharmacyId)
  // D-VIEWCOLS-4 phase 2: complete PioneerRx path list for the list-column source picker.
  const [pioneerPaths, setPioneerPaths] = useState<api.PioneerPath[]>([])
  useEffect(() => { api.listPioneerPaths().then(r => setPioneerPaths(r.paths ?? [])).catch(() => {}) }, [])
  const [source, setSource] = useState<'default' | 'custom'>('default')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  // RBAC / View-as
  const [roles, setRoles] = useState<{ id: string; name: string; is_system: boolean }[]>([])
  const [catalogDefault, setCatalogDefault] = useState<Record<string, string>>({}) // table.column -> full|masked|hidden
  const [viewAsRole, setViewAsRole] = useState<string>('') // '' = admin (full)
  const [viewRules, setViewRules] = useState<Record<string, string>>({}) // table.column -> access

  useEffect(() => {
    api.listPharmacies().then(r => {
      setPharmacies(r.items.map(p => ({ id: p.id, name: p.name })))
      if (r.items.length && !pharmacyId) setPharmacyId(r.items[0].id)
    }).catch(e => setError((e?.message || 'Failed to load pharmacies')))
    api.listPioneerPaths().then(r => setPaths(r.paths)).catch(() => {})
    // Canonical built-in column -> PioneerRx path map, so every non-custom field shows its
    // real source (not "standard"). Naming drift + composites reconciled at render time.
    api.listFieldMappingDefaults().then(r => {
      const m: Record<string, string> = {}
      for (const d of (r.defaults ?? [])) if (d.pioneer_path) m[`${d.owlrx_table}.${d.owlrx_column}`] = d.pioneer_path
      setFieldDefaults(m)
    }).catch(() => {})
    // RBAC reference data for View-as + card gating (token-authed via api client).
    api.rbacListRoles().then(d => setRoles(d.items ?? [])).catch(() => {})
    api.rbacFieldCatalog().then(d => {
      const m: Record<string, string> = {}
      for (const f of (d.fields ?? [])) m[`${f.owlrx_table}.${f.owlrx_column}`] = f.default_access
      setCatalogDefault(m)
    }).catch(() => {})
  }, [])

  // Load the selected role's field rules for View-as field masking/hiding.
  useEffect(() => {
    if (!viewAsRole) { setViewRules({}); return }
    let cancelled = false
    api.rbacRoleFieldRules(viewAsRole)
      .then(rr => {
        if (cancelled) return
        const m: Record<string, string> = {}
        for (const r of (rr.rules ?? [])) m[`${r.owlrx_table}.${r.owlrx_column}`] = r.access
        setViewRules(m)
      }).catch(() => {})
    return () => { cancelled = true }
  }, [viewAsRole])

  useEffect(() => { if (pharmacyId) void load(pharmacyId) }, [pharmacyId, surface])

  // D-SOC-9: 'facility' is this page's TAB name, not a server surface. Its CARDS live on
  // 'fac_home' (the Home tiles); its column lists are separate surfaces handled by useListSurface.
  // Sending 'facility' to the API would be rejected by ValidSurfaces and silently fall back to
  // 'rx' — which is exactly the class of silent failure that cost five bug reports.
  const cardSurface = surface === 'facility' ? 'fac_home' : surface

  async function load(pid: string) {
    setLoading(true); setError(null); setNotice(null)
    try {
      const [layout, maps, mods] = await Promise.all([
        api.getRxViewLayout(pid, cardSurface),
        api.listFieldMappingsFor(pid),
        api.listPharmacyModules(pid).catch(() => ({ modules: [] as api.PharmacyModule[] })),
      ])
      const enabledModules = new Set(mods.modules.filter(m => m.effective_enabled).map(m => m.module_key))
      setPharmacyModules(enabledModules)
      setSource(layout.source)
      // DEFAULT_LAYOUTS mirrors the detail page. Build a RxViewCard from a DefCard,
      // attaching each field's PioneerRx mapping where one exists so the picker shows it mapped.
      const def = (DEFAULT_LAYOUTS[surface] ?? DEFAULT_LAYOUTS.rx).filter(c => !c.module || enabledModules.has(c.module))
      const mapByCol = new Map(maps.items.map(m => [`${m.owlrx_table}.${m.owlrx_column}`, m]))
      const defToCard = (c: DefCard, pos: number): RxViewCard => ({
        card_key: c.key, title: c.title, position: pos, visible: true,
        kind: c.kind, required_permission: null, allowed_roles: [],
        fields: (c.fields ?? []).map((f, fi) => {
          const m = mapByCol.get(`${f.table}.${f.col}`)
          return {
            id: m?.id, owlrx_table: f.table, owlrx_column: f.col, owlrx_label: f.label,
            pioneer_path: m?.pioneer_path ?? null, card_position: fi, visible: true,
            // drug_reference fields are canonical module fields resolved by their own endpoint —
            // never mark them custom, or save creates a deny-by-default field_catalog row that
            // hides them from every role (D-DRUGREF-3).
            custom: f.table === 'drug_reference' ? false : !m?.pioneer_path,
          }
        }),
      })
      if (layout.source === 'custom') {
        const saved = sortCards(layout.cards)
        // Self-heal: a pharmacy whose saved layout predates a newly-added default card
        // (e.g. the promoted Delivery/Claims/Fills/Rx-comments/Timeline sections) would
        // otherwise never see it in the builder. Surface any missing default cards,
        // appended visible + all-roles so a no-op save preserves current behavior; the
        // admin can then hide / role-gate / reorder them.
        const have = new Set(saved.map(c => c.card_key))
        const missing = def.filter(c => !have.has(c.key)).map((c, i) => defToCard(c, saved.length + i))
        setCards([...saved, ...missing])
        if (missing.length) setNotice(`${missing.length} new default card${missing.length > 1 ? 's' : ''} surfaced (not yet saved): ${missing.map(c => c.title).join(', ')}. Save to apply.`)
      } else {
        // No saved layout: pre-fill the builder with the pharmacy's ACTUAL current view.
        setCards(def.map((c, ci) => defToCard(c, ci)))
        setNotice("This pharmacy has no saved layout yet — showing its current view. Edit and Save to customize.")
      }
    } catch (e: any) {
      setError((e?.message || 'Failed to load layout'))
      setCards([])
    } finally { setLoading(false) }
  }

  function sortCards(cs: RxViewCard[]) {
    return [...cs].sort((a, b) => a.position - b.position)
      .map(c => ({ ...c, fields: [...(c.fields ?? [])].sort((a, b) => a.card_position - b.card_position) }))
  }

  // ---- mutations (local) ----
  const update = (fn: (cs: RxViewCard[]) => RxViewCard[]) => setCards(cs => fn(structuredClone(cs)))
  const moveCard = (i: number, dir: -1 | 1) => update(cs => {
    const j = i + dir; if (j < 0 || j >= cs.length) return cs
    ;[cs[i], cs[j]] = [cs[j], cs[i]]; cs.forEach((c, k) => (c.position = k)); return cs
  })
  const toggleCard = (i: number) => update(cs => { cs[i].visible = !cs[i].visible; return cs })
  const renameCard = (i: number, t: string) => update(cs => { cs[i].title = t; return cs })
  const removeCard = (i: number) => { if (confirm(`Remove card "${cards[i].title}"?`)) update(cs => { cs.splice(i, 1); cs.forEach((c, k) => (c.position = k)); return cs }) }
  const addCard = () => { const t = prompt('New card title:'); if (!t) return; update(cs => { cs.push({ card_key: `custom_${Date.now()}`, title: t, position: cs.length, visible: true, kind: 'fields', required_permission: null, allowed_roles: [], fields: [] }); return cs }) }
  // Home surface: sections are component-rendered — a free-form fields card would be
  // silently ignored by the home page, so "add" means picking a KNOWN section that is
  // not currently in the layout (opt-in ones like action_aging, or one that was removed).
  const addHomeSection = (key: string) => {
    // D-HOME-2 part B: '__metric__' creates a configurable metric card (count/sum/avg/…
    // over an allowlisted source) rendered by OwnerHome via /api/dashboards/home-metrics.
    if (key === '__metric__') {
      const t = prompt('Metric card title:'); if (!t) return
      update(cs => { cs.push({ card_key: `metric_${Date.now()}`, title: t, position: cs.length, visible: true, kind: 'home_metric', required_permission: null, allowed_roles: [], fields: [], config: { metric: { source: 'rx_items', agg: 'count', filters: [], display: 'number' } } }); return cs })
      return
    }
    // ASP-53: '__view__' creates a custom-view tile — a saved view's live count with
    // click-through to /rx/view/:id. The view is picked in the card's editor below.
    if (key === '__view__') {
      update(cs => { cs.push({ card_key: `view_${Date.now()}`, title: 'Custom view tile', position: cs.length, visible: true, kind: 'custom_view', required_permission: null, allowed_roles: [], fields: [], config: { view: {} } }); return cs })
      return
    }
    const spec = HOME_SECTION_CATALOG.find(s => s.key === key) ?? FAC_HOME_CATALOG.find(s => s.key === key)
    if (!spec || cards.some(c => c.card_key === key)) return
    update(cs => { cs.push({ card_key: spec.key, title: spec.title, position: cs.length, visible: true, kind: 'home_section', required_permission: null, allowed_roles: [], fields: [] }); return cs })
  }
  const missingHomeSections = surface === 'home' ? HOME_SECTION_CATALOG.filter(s => !cards.some(c => c.card_key === s.key)) : []

  const toggleField = (ci: number, fi: number) => update(cs => { cs[ci].fields[fi].visible = !cs[ci].fields[fi].visible; return cs })
  const mapField = (ci: number, fi: number, path: string) => { mappingsDirty.current = true; update(cs => { cs[ci].fields[fi].pioneer_path = path || null; cs[ci].fields[fi].custom = !path; return cs }) }
  const delField = (ci: number, fi: number) => update(cs => { cs[ci].fields.splice(fi, 1); cs[ci].fields.forEach((f, k) => (f.card_position = k)); return cs })
  // D-DRUGREF-3: canonical DrugReference fields addable onto ANY fields-card (module-gated).
  const DRUGREF_FIELDS: { col: string; label: string }[] = [
    { col: 'category', label: 'Drug category' },
    { col: 'rems_program', label: 'REMS program' },
    { col: 'days_before_procedure', label: 'Days before procedure' },
    { col: 'copay_program', label: 'Copay program' },
  ]
  const addDrugRefField = (ci: number, col: string) => update(cs => {
    const meta = DRUGREF_FIELDS.find(f => f.col === col)
    if (!meta || cs[ci].fields.some(f => f.owlrx_table === 'drug_reference' && f.owlrx_column === col)) return cs
    cs[ci].fields.push({ owlrx_table: 'drug_reference', owlrx_column: col, owlrx_label: meta.label, pioneer_path: null, card_position: cs[ci].fields.length, visible: true, custom: false, data_type: col === 'days_before_procedure' ? 'number' : 'text' })
    return cs
  })
  // Add a blank, inline-editable custom field row (no popup).
  const addField = (ci: number) => update(cs => { cs[ci].fields.push({ owlrx_table: 'custom', owlrx_column: `custom_${Date.now()}`, owlrx_label: '', pioneer_path: null, card_position: cs[ci].fields.length, visible: true, custom: true, data_type: 'text' }); return cs })
  const setFieldType = (ci: number, fi: number, dt: string) => update(cs => { cs[ci].fields[fi].data_type = dt; return cs })
  // ASP-43: options[] + default_value live in the field meta inside config_json (zero schema).
  const setFieldOptions = (ci: number, fi: number, raw: string) => update(cs => {
    const opts = raw.split(/[\n,]/).map(x => x.trim()).filter(Boolean)
    cs[ci].fields[fi].options = opts
    // Drop a default that no longer exists in the list — a default matching nothing is
    // worse than none, because the card silently shows blank and nobody knows why.
    const cur = cs[ci].fields[fi].default_value
    if (cur && opts.length > 0 && !opts.includes(cur)) cs[ci].fields[fi].default_value = ''
    return cs
  })
  const setFieldDefault = (ci: number, fi: number, v: string) => update(cs => { cs[ci].fields[fi].default_value = v; return cs })
  const renameField = (ci: number, fi: number, label: string) => update(cs => {
    cs[ci].fields[fi].owlrx_label = label
    // For custom (unmapped) fields the column key tracks the label slug.
    if (cs[ci].fields[fi].custom) cs[ci].fields[fi].owlrx_column = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 60) || cs[ci].fields[fi].owlrx_column
    return cs
  })

  async function save() {
    setSaving(true); setError(null); setNotice(null)
    // RCA-ASP-66 F5: the COLUMN LISTS persist FIRST (they're what admins actually edit) and
    // every leg is individually guarded — one failing leg can never silently eat another's
    // edits (root cause B: the old chain ran the detail PUT first; any failure or mid-save
    // refresh aborted before the list persists, so deletions never reached the server).
    const failed: string[] = []
    const leg = async (name: string, fn: () => Promise<any>) => { try { await fn() } catch { failed.push(name) } }
    try {
      await leg('Pharmacy list columns', () => pharmacyList.persist())
      await leg('Provider list columns', () => providerList.persist())
      await leg('Patient Rx list', () => patientRxList.persist())
      await leg('Provider patient Rx list', () => providerPtRxList.persist())
      await leg('Prescriber Rx list', () => prescRxList.persist())
      // D-SOC-8: the facility list persists like every other column list. Its panel and hook
      // shipped in #2710 without this line, so toggling a facility column changed the UI, "Save
      // layout" reported success, and nothing was ever written to pharmacy_view_fields — the
      // portal kept rendering defaults. A surface that renders but does not persist looks
      // identical to a working one until someone checks the database.
      await leg('Facility Rx list', () => facRxList.persist())
      await leg('Facility patients list', () => facPtList.persist())
      const out = cards.map((c, i) => ({ ...c, position: i, fields: c.fields.map((f, k) => ({ ...f, card_position: k })) }))
      await leg(surface === 'facility' ? 'Facility Home tiles' : 'Detail cards', () => api.putRxViewLayout(out, pharmacyId, cardSurface))
      await load(pharmacyId) // reflect exactly what persisted
      if (failed.length) setError(`Saved with failures — these did NOT save: ${failed.join(', ')}. Fix and Save again.`)
      else setNotice('Saved.')
    } catch (e: any) { setError((e?.message || 'Save failed')); setSaving(false); return }
    finally { setSaving(false) }
    // ASP-38: a mapping changed this session — re-extract so existing Rx reflect the new
    // source immediately. Runs AFTER Save completes (its own progress line; the Save button
    // never sticks on it — 'Savings get stuck' fix, Patrick 2026-07-22).
    if (mappingsDirty.current) {
      setNotice('Saved. Filling the changed columns for existing prescriptions on the server…')
      try {
        await startBackfill.mutateAsync('save')
        setNotice('Saved. Existing prescriptions are being filled on the server; progress is shown above. You can leave this page.')
        mappingsDirty.current = false
      } catch { setNotice('Saved — but the backfill did not start. Use "Run now" to retry.') }
    }
  }

  // View-as: effective access for a field as the selected role would see it.
  const effAccess = (f: RxViewField): 'full' | 'masked' | 'hidden' => {
    if (!viewAsRole) return 'full'
    const k = `${f.owlrx_table}.${f.owlrx_column}`
    return (viewRules[k] ?? catalogDefault[k] ?? (f.custom ? 'hidden' : 'full')) as any
  }
  const cardGatedOut = (c: RxViewCard) => !!viewAsRole && (
    (c.denied_roles ?? []).includes(viewAsRole) ||
    (!!(c.allowed_roles?.length) && !c.allowed_roles.includes(viewAsRole))
  )
  // Region the card renders in (default body). Mirrors the detail-page renderers.
  const cardRegion = (c: RxViewCard): 'header' | 'body' | 'footer' => (c.config?.region ?? 'body') as any
  // Render one preview card (sample data, role masking/hiding applied). Returns null
  // when a fields card has nothing visible under the selected role.
  const renderPvCard = (c: RxViewCard) => {
    const vis = c.fields.filter(f => f.visible && effAccess(f) !== 'hidden')
    if (viewAsRole && c.kind === 'fields' && vis.length === 0) return null
    // Honest per-section previews for the home surface (D-HOME-2 part A) — each card
    // states the metric it computes today instead of a uniform placeholder. Numbers on
    // the real page are computed live server-side; sample tiles below are illustrative.
    if (c.kind === 'home_metric') {
      return (
        <div key={c.card_key} style={pvCard}>
          <div style={pvHead}>{c.title}</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)', margin: '4px 0' }}>{c.config?.metric?.group_by ? '▦ tiles' : '123'}</div>
          <div style={{ ...pvMuted, fontWeight: 600 }}>{metricSummary(c.config)}</div>
          <div style={pvMuted}>Value computed live server-side. Clicking the card on the home page opens the prescription list with exactly these filters pre-applied (a group tile also pins its group).</div>
        </div>
      )
    }
    if (c.kind === 'custom_view') {
      return (
        <div key={c.card_key} style={pvCard}>
          <div style={pvHead}>{c.title}</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)', margin: '4px 0' }}>123</div>
          <div style={{ ...pvMuted, fontWeight: 600 }}>{c.config?.view?.view_name ? `Saved view: ${c.config.view.view_name}` : 'No view selected yet — pick one in the card editor'}</div>
          <div style={pvMuted}>Live count of the view's rows, computed server-side. Clicking the tile opens the view's worklist (exactly the counted rows).</div>
        </div>
      )
    }
    if (c.kind === 'home_section') {
      const spec = HOME_SECTION_CATALOG.find(s => s.key === c.card_key) ?? FAC_HOME_CATALOG.find(s => s.key === c.card_key)
      const paramWiring = BUILTIN_PARAM_DEFS[c.card_key]?.wiring(c.config?.params ?? {})
      return (
        <div key={c.card_key} style={pvCard}>
          <div style={pvHead}>{c.title}</div>
          {c.card_key === 'status_pulse' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, margin: '6px 0' }}>
              {['Fill In Progress', 'On Hold', 'Refills Remaining', 'Discontinued', 'Expired or No Refills', 'Fill Requested', 'Transferred'].map(t => (
                <span key={t} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text-soft)' }}>{t}</span>
              ))}
            </div>
          )}
          <div style={{ ...pvMuted, fontWeight: 600 }}>{paramWiring ?? spec?.metric ?? 'Component-rendered section'}</div>
          {spec?.body && <div style={pvMuted}>{spec.body}</div>}
        </div>
      )
    }
    return (
      <div key={c.card_key} style={pvCard}>
        <div style={pvHead}>{c.title}{c.config?.header_band ? ' · identity band' : ''}</div>
        {c.kind !== 'fields'
          ? <div style={pvMuted}>{c.kind === 'workbench' ? 'Status dropdowns (workflow)' : c.kind === 'financials' ? 'Role-gated financials' : 'Alerts feed'}</div>
          : vis.length === 0 ? <div style={pvMuted}>— no visible fields —</div>
          : vis.map(f => {
            const acc = effAccess(f)
            const sample = sampleFor(f)
            const display = acc === 'masked' ? maskSample(sample) : sample
            return (
              <div key={`${f.owlrx_table}.${f.owlrx_column}`} style={pvField}>
                <span style={{ color: 'var(--color-text-muted)' }}>{f.owlrx_label}</span>
                <span style={{ color: acc === 'masked' ? '#7a8694' : 'inherit', fontFamily: acc === 'masked' ? 'monospace' : 'inherit' }}>
                  {display}
                </span>
              </div>
            )
          })}
      </div>
    )
  }
  const toggleCardRole = (i: number, roleId: string) => update(cs => {
    const cur = new Set(cs[i].allowed_roles ?? [])
    if (cur.has(roleId)) cur.delete(roleId); else cur.add(roleId)
    cs[i].allowed_roles = [...cur]
    return cs
  })
  // Denylist: HIDE the card from these roles. Deny wins over allow at render.
  const toggleCardDeniedRole = (i: number, roleId: string) => update(cs => {
    const cur = new Set(cs[i].denied_roles ?? [])
    if (cur.has(roleId)) cur.delete(roleId); else cur.add(roleId)
    cs[i].denied_roles = [...cur]
    return cs
  })
  const setCardConfig = (i: number, patch: Record<string, any>) => update(cs => {
    cs[i].config = { ...(cs[i].config ?? {}), ...patch }; return cs
  })
  // D-HOME-3: when a built-in card's params change and its title still matches the
  // auto pattern, regenerate the title so the page never claims "24h" while counting 48h.
  // A custom (renamed) title is the admin's — left alone.
  const setBuiltinParams = (i: number, cardKey: string, patch: Record<string, any>) => update(cs => {
    const def = BUILTIN_PARAM_DEFS[cardKey]
    const oldP = cs[i].config?.params ?? {}
    const nextP = { ...oldP, ...(patch.params ?? {}) }
    if (def && cs[i].title === def.defaultTitle(oldP)) cs[i].title = def.defaultTitle(nextP)
    cs[i].config = { ...(cs[i].config ?? {}), params: nextP }
    return cs
  })
  const moveField = (ci: number, fi: number, dir: -1 | 1) => update(cs => {
    const fs = cs[ci].fields; const j = fi + dir; if (j < 0 || j >= fs.length) return cs
    ;[fs[fi], fs[j]] = [fs[j], fs[fi]]; fs.forEach((f, k) => (f.card_position = k)); return cs
  })

  // ---- header / footer composition ----
  // Header and footer are fully composable field regions (same editor as body
  // cards: add/remove/reorder/relabel any field and map it to a PioneerRx source).
  // The header is SEEDED from the current identity band so a pharmacy starts from
  // today's view and edits freely; while no header card exists the page renders
  // the built-in band unchanged.
  const bandDefs = BAND_FIELDS[surface] ?? []
  const hasHeaderCard = cards.some(c => (c.config?.region ?? 'body') === 'header')
  const seedHeaderFromCurrent = () => update(cs => {
    if (cs.some(c => (c.config?.region ?? 'body') === 'header')) return cs
    const fields = ([{ owlrx_table: bandDefs[0]?.table ?? 'patients', owlrx_column: 'display_name', owlrx_label: BAND_NAME_LABEL[surface] ?? 'Name', visible: true, card_position: 0 }] as any[])
      .concat(bandDefs.filter(d => !d.optional).map((d, k) => ({ owlrx_table: d.table, owlrx_column: d.col, owlrx_label: d.label, visible: true, card_position: k + 1 })))
    cs.unshift({ card_key: 'header_band', title: 'Header', position: 0, visible: true,
      kind: 'fields', required_permission: null, allowed_roles: [], denied_roles: [],
      config: { region: 'header', header_band: true }, fields } as any)
    return cs
  })
  const addRegionCard = (region: 'header' | 'footer') => update(cs => {
    cs.push({ card_key: `custom_${Date.now()}`, title: region === 'header' ? 'Header' : 'Footer',
      position: cs.length, visible: true, kind: 'fields', required_permission: null,
      allowed_roles: [], denied_roles: [], config: { region, ...(region === 'header' ? { header_band: true } : {}) }, fields: [] } as any)
    return cs
  })

  // Drag-to-reorder for cards + header fields (same ASP ColumnManager pattern; arrows kept).
  const cardKey = (c: RxViewCard) => c.card_key
  const cardDrag = usePointerReorder((from, to) => update(cs =>
    reorderByKey(cs, cardKey, from, to).map((c, i) => ({ ...c, position: i }))))
  const fieldKey = (ck: string, col: string) => `${ck}\x1f${col}`
  const fieldDrag = usePointerReorder((from, to) => {
    const ck = from.split('\x1f')[0]
    update(cs => cs.map(c => c.card_key === ck
      ? { ...c, fields: reorderByKey(c.fields, f => fieldKey(ck, (f as any).owlrx_column), from, to).map((f, k) => ({ ...f, card_position: k })) }
      : c))
  })

  const renderCardRow = (c: RxViewCard, ci: number) => (
    <div key={c.card_key} {...cardDrag.rowProps(c.card_key)} style={{ ...cardBox, outline: cardDrag.isDragging(c.card_key) ? '1.5px solid var(--color-accent, #0f766e)' : (cardBox as any).outline }}>
      <div style={cardHead}>
        <span {...cardDrag.handleProps(c.card_key)}><GripVertical size={13} style={{ color: 'var(--color-text-mute)' }} /></span>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <button onClick={() => moveCard(ci, -1)} disabled={ci === 0} style={miniBtn} aria-label="Move up"><ChevronUp size={13} /></button>
          <button onClick={() => moveCard(ci, 1)} disabled={ci === cards.length - 1} style={miniBtn} aria-label="Move down"><ChevronDown size={13} /></button>
        </div>
        <input value={c.title} onChange={e => renameCard(ci, e.target.value)} style={titleInput} />
        <select value={c.config?.region ?? 'body'}
                onChange={e => setCardConfig(ci, { region: e.target.value })}
                title="Which region this card renders in"
                aria-label="Card region"
                style={{ fontSize: 12, padding: '3px 6px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)' }}>
          <option value="header">Header</option>
          <option value="body">Body</option>
          <option value="footer">Footer</option>
        </select>
        {c.kind !== 'fields' && <span style={kindPill}>{c.kind}</span>}
        <button onClick={() => toggleCard(ci)} style={iconBtn} title={c.visible ? 'Hide card' : 'Show card'}>
          {c.visible ? <Eye size={15} /> : <EyeOff size={15} />}
        </button>
        <button onClick={() => addField(ci)} style={miniTextBtn} disabled={c.kind !== 'fields'}>+ field</button>
        {c.kind === 'fields' && pharmacyModules.has('drugreference') && (
          <SearchableSelect value="" onChange={col => col && addDrugRefField(ci, col)} ariaLabel="Add DrugReference field" placeholder="+ DrugReference" style={{ minWidth: 150, display: 'inline-block', marginLeft: 6 }}
            options={DRUGREF_FIELDS.map(f => ({ value: f.col, label: f.label }))} />
        )}
        <button onClick={() => removeCard(ci)} style={{ ...iconBtn, color: '#b3261e' }} title="Remove card"><Trash2 size={14} /></button>
      </div>
      <div style={accessRow}>
        <span style={{ color: 'var(--color-text-muted)' }}>Visible to roles:</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, flex: 1 }}>
          {roles.filter(r => r.name !== 'platform_admin').map(r => {
            const on = (c.allowed_roles ?? []).includes(r.id)
            return (
              <button key={r.id} type="button" onClick={() => toggleCardRole(ci, r.id)}
                style={{ ...roleChip, ...(on ? roleChipOn : {}) }}
                title={on ? 'Click to remove this role' : 'Click to allow this role'}>
                {r.name}
              </button>
            )
          })}
          {(c.allowed_roles ?? []).length === 0 && (
            <span style={{ fontSize: 11, color: 'var(--color-text-muted)', alignSelf: 'center' }}>
              All roles — no card-level restriction (field-level RBAC still applies)
            </span>
          )}
        </div>
      </div>
      <div style={accessRow}>
        <span style={{ color: 'var(--color-text-muted)' }}>Hidden from roles:</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, flex: 1 }}>
          {roles.filter(r => r.name !== 'platform_admin').map(r => {
            const on = (c.denied_roles ?? []).includes(r.id)
            return (
              <button key={r.id} type="button" onClick={() => toggleCardDeniedRole(ci, r.id)}
                style={{ ...roleChip, ...(on ? { background: '#fde8e6', borderColor: '#dc2626', color: '#b3261e' } : {}) }}
                title={on ? 'Click to stop hiding from this role' : 'Click to hide this card from this role'}>
                {r.name}
              </button>
            )
          })}
          {(c.denied_roles ?? []).length === 0 && (
            <span style={{ fontSize: 11, color: 'var(--color-text-muted)', alignSelf: 'center' }}>
              Not hidden from any role
            </span>
          )}
        </div>
      </div>
      {c.kind === 'clinical_comments' && (
        <div style={accessRow}>
          <span style={{ color: 'var(--color-text-muted)' }}>Comments shown:</span>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <input type="checkbox" checked={!!c.config?.informational_only}
              onChange={e => setCardConfig(ci, { informational_only: e.target.checked })} />
            Informational only (hides critical / point-of-sale / delivery notes)
          </label>
        </div>
      )}
      {c.kind === 'home_metric' ? (
        <MetricEditor config={c.config} onChange={patch => setCardConfig(ci, patch)} />
      ) : c.kind === 'custom_view' ? (
        <CustomViewTileEditor pharmacyId={pharmacyId} config={c.config} onChange={patch => setCardConfig(ci, patch)} onAutoTitle={t => { if (!c.title || c.title === 'Custom view tile') renameCard(ci, t) }} />
      ) : c.kind === 'home_section' && c.card_key === 'status_pulse' ? (
        <PulseTileEditor config={c.config} onChange={patch => setCardConfig(ci, patch)} />
      ) : c.kind === 'home_section' && BUILTIN_PARAM_DEFS[c.card_key] ? (
        <BuiltinParamsEditor cardKey={c.card_key} config={c.config} onChange={patch => setBuiltinParams(ci, c.card_key, patch)} />
      ) : c.kind !== 'fields' ? (
        <div style={specialRow}>Managed by the {c.title} component — show/hide, reorder &amp; role-gate only.</div>
      ) : c.fields.length === 0 ? (
        <div style={specialRow}>No fields. Use “+ field”.</div>
      ) : c.fields.map((f, fi) => { const canon = canonicalPath(fieldDefaults, f); return (
        <div key={`${c.card_key}-${fi}`} {...fieldDrag.rowProps(fieldKey(c.card_key, (f as any).owlrx_column))} style={{ ...fieldRow, opacity: f.visible ? 1 : 0.45, outline: fieldDrag.isDragging(fieldKey(c.card_key, (f as any).owlrx_column)) ? '1.5px solid var(--color-accent, #0f766e)' : 'none' }}>
          <span {...fieldDrag.handleProps(fieldKey(c.card_key, (f as any).owlrx_column))}><GripVertical size={11} style={{ color: 'var(--color-text-mute)' }} /></span>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <button onClick={() => moveField(ci, fi, -1)} disabled={fi === 0} style={miniBtn} aria-label="Move field up"><ChevronUp size={11} /></button>
            <button onClick={() => moveField(ci, fi, 1)} disabled={fi === c.fields.length - 1} style={miniBtn} aria-label="Move field down"><ChevronDown size={11} /></button>
          </div>
          <button onClick={() => toggleField(ci, fi)} style={iconBtn} title={f.visible ? 'Hide' : 'Show'}>
            {f.visible ? <Eye size={14} /> : <EyeOff size={14} />}
          </button>
          <input
            value={f.owlrx_label}
            onChange={e => renameField(ci, fi, e.target.value)}
            placeholder="Field name"
            style={labelInput}
          />
          <PathSelect value={f.pioneer_path} paths={paths} onChange={p => mapField(ci, fi, p)}
            bindingLabel={f.owlrx_table !== 'custom' ? (canon || '— no PioneerRx source —') : null} />
          {(f.owlrx_table === 'custom' && !f.pioneer_path) && (<>
            <select value={f.data_type || 'text'} onChange={e => setFieldType(ci, fi, e.target.value)} title="Field type"
              style={{ fontSize: 11.5, padding: '3px 6px', borderRadius: 7, border: '1px solid var(--color-border,#DED8CC)', background: 'var(--color-surface,#fff)' }}>
              <option value="text">Text</option>
              <option value="date">Date</option>
              <option value="multi_text">Multi-text</option>
            </select>
            {(f.data_type || 'text') === 'text' && (<>
              <input value={(f.options ?? []).join(', ')} onChange={e => setFieldOptions(ci, fi, e.target.value)}
                placeholder="Options (comma-separated)" title="ASP-43: dropdown options — leave empty for free text"
                style={{ fontSize: 11.5, padding: '3px 6px', borderRadius: 7, border: '1px solid var(--color-border,#DED8CC)', background: 'var(--color-surface,#fff)', minWidth: 150 }} />
              {/* ASP-81: when the field HAS options, the default must be picked FROM them —
                  typing it free-hand invited typos that silently never matched a choice. */}
              {(f.options ?? []).length > 0
                ? <select value={f.default_value ?? ''} onChange={e => setFieldDefault(ci, fi, e.target.value)}
                    title="Fallback default for THIS card only. ASP-82: pharmacies now set the field default themselves in Settings → Custom fields (starred option), which applies everywhere. Leave blank unless this card needs to differ."
                    style={{ fontSize: 11.5, padding: '3px 6px', borderRadius: 7, border: '1px solid var(--color-border,#DED8CC)', background: 'var(--color-surface,#fff)', minWidth: 110 }}>
                    <option value="">No default</option>
                    {(f.options ?? []).map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                : <input value={f.default_value ?? ''} onChange={e => setFieldDefault(ci, fi, e.target.value)}
                    placeholder="Default" title="Shown when no value is set yet (display-only; nothing auto-writes)"
                    style={{ fontSize: 11.5, padding: '3px 6px', borderRadius: 7, border: '1px solid var(--color-border,#DED8CC)', background: 'var(--color-surface,#fff)', width: 110 }} />}
            </>)}
          </>)}
          <span style={f.pioneer_path ? defaultPill : f.owlrx_table === 'custom' ? customPill : canon ? autoPill : standardPill}>{f.pioneer_path ? 'mapped' : f.owlrx_table === 'custom' ? 'custom' : canon ? 'mapped' : 'no source'}</span>
          <button onClick={() => delField(ci, fi)} style={{ ...iconBtn, color: '#b3261e' }} title="Remove field"><Trash2 size={13} /></button>
        </div>
      ) })}
    </div>
  )

  const exitListTab = () => { const next = new URLSearchParams(sp); next.delete('listtab'); setSp(next) }
  const saveTabColumns = async () => {
    setSaving(true); setError(null); setNotice(null)
    try { await pharmacyList.persist(); setNotice('Columns saved for this tab — they now override the shared columns for this tab only.') }
    catch (e: any) { setError(e?.message || 'Save failed') }
    finally { setSaving(false) }
  }
  const resetTabOverride = async () => {
    if (!listTab || !pharmacyId) return
    if (!window.confirm(`Remove the ${LIST_TAB_LABELS[listTab] ?? listTab} tab's column override? This tab will follow the shared Rx columns again.`)) return
    setSaving(true); setError(null); setNotice(null)
    try { await api.putRxViewLayout([], pharmacyId, `rx_list:${listTab}`); setNotice('Override removed — this tab now follows the shared columns.'); exitListTab() }
    catch (e: any) { setError(e?.message || 'Reset failed') }
    finally { setSaving(false) }
  }

  if (listTab) {
    const tabLabel = LIST_TAB_LABELS[listTab] ?? listTab
    return (
      <div style={{ padding: '4px 0 40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
          <button onClick={exitListTab} style={secondaryBtn}>← Back to views</button>
          <h1 style={{ fontSize: 20, margin: 0 }}>Columns · {tabLabel} tab</h1>
          <span style={badge}>SaaS admin · per pharmacy</span>
          <div style={{ flex: 1 }} />
          <label style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Pharmacy</label>
          <select value={pharmacyId} onChange={e => setPharmacyId(e.target.value)} style={selectStyle}>
            {pharmacies.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button onClick={saveTabColumns} disabled={saving || loading || !cards.length} style={primaryBtn}>{saving ? 'Saving…' : 'Save columns'}</button>
          <button onClick={resetTabOverride} disabled={saving || loading} style={secondaryBtn} title="Delete this tab's override so it follows the shared columns again">Reset to shared</button>
        </div>
        <p style={{ fontSize: 12.5, color: 'var(--color-text-muted)', maxWidth: '100%', marginTop: 0 }}>
          These columns apply to the <strong>{tabLabel}</strong> tab only. Every other tab keeps the shared Rx columns —
          editing those (the main Rx builder) changes every tab that has no override of its own. This override is a
          <strong> snapshot</strong>: it will not follow later changes to the shared columns. Use <strong>Reset to shared</strong> to
          remove it so this tab follows the shared columns again. Field-level RBAC still applies per role regardless of which columns are shown.
        </p>
        {error && <div style={errorBox}>{error}</div>}
        {notice && <div style={noticeBox}>{notice}</div>}
        <ListColumnsPanel
          title={`${tabLabel} tab columns`}
          hint="Columns shown on this tab's list for this pharmacy — reorder, show/hide, rename, repoint. Saved as a per-tab override."
          ls={pharmacyList} catalog={listCatalogGated} customSources={customSources} showProv={true} surface={surface} pioneerPaths={pioneerPaths} pharmacyId={pharmacyId} onMappingChanged={() => { mappingsDirty.current = true }} />
      </div>
    )
  }

  return (
    <div style={{ padding: '4px 0 40px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <h1 style={{ fontSize: 20, margin: 0 }}>{SURFACE_TITLES[surface] ?? 'View builder'}</h1>
        <span style={badge}>SaaS admin · per pharmacy</span>
        <span style={{ fontSize: 11.5, color: 'var(--color-text-muted)' }}>
          {source === 'custom' ? 'saved layout' : 'using built-in defaults'}
        </span>
        <div style={{ flex: 1 }} />
        <label style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Pharmacy</label>
        <select value={pharmacyId} onChange={e => setPharmacyId(e.target.value)} style={selectStyle}>
          {pharmacies.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <button onClick={save} disabled={saving || loading || !cards.length} style={primaryBtn}>
          {saving ? 'Saving…' : 'Save layout'}
        </button>
        {surface === 'home' ? (
          <select value="" disabled={loading} style={selectStyle}
                  title="Add a built-in section this pharmacy does not currently show, or a new configurable metric card"
                  onChange={e => { if (e.target.value) addHomeSection(e.target.value) }}>
            <option value="">+ Add section…</option>
            <option value="__metric__">New metric card (count / sum / avg…)</option>
            <option value="__view__">Custom view tile (saved view count + link)</option>
            {missingHomeSections.map(s => <option key={s.key} value={s.key}>{s.title}{s.optIn ? ' (opt-in)' : ''}</option>)}
          </select>
        ) : surface === 'facility' ? (
          /* D-SOC-9: the facility Home tiles. A fixed catalogue, not free-form metric cards —
             each entry maps to a number facility/summary actually returns, so a card can never
             promise data the endpoint does not have. */
          <select value="" disabled={loading || !missingFacHome.length} style={selectStyle}
                  title="Add a Home tile this pharmacy's facility portal does not currently show"
                  onChange={e => { if (e.target.value) addHomeSection(e.target.value) }}>
            <option value="">{missingFacHome.length ? '+ Add Home tile…' : 'All Home tiles added'}</option>
            {missingFacHome.map(s => <option key={s.key} value={s.key}>{s.title}</option>)}
          </select>
        ) : (
          <button onClick={addCard} disabled={loading} style={secondaryBtn}><Plus size={14} /> Add card</button>
        )}
      </div>

      {/* keyed: an unsaved schedule edit must never carry over to another pharmacy */}
      {pharmacyId && <MappingBackfillPanel key={pharmacyId} pharmacyId={pharmacyId} />}

      {surface === 'rx' && pharmacyId && <ViewManagerPanel pharmacyId={pharmacyId} />}

      {surface === 'home' ? (
        <p style={{ fontSize: 12.5, color: 'var(--color-text-muted)', maxWidth: '100%', marginTop: 0 }}>
          Sections you compose here drive this pharmacy's Home page: show/hide, reorder, rename, and role-gate
          each section, or add opt-in sections via “Add section”. The <em>numbers</em> each section shows are
          computed live server-side and are not yet configurable here — per-card metric &amp; aggregation
          (count/sum/avg…) is planned (D-HOME-2). Role gates still apply at render.
        </p>
      ) : (
      <p style={{ fontSize: 12.5, color: 'var(--color-text-muted)', maxWidth: '100%', marginTop: 0 }}>
        Cards and fields you compose here drive the Rx detail view for this pharmacy. Each field maps to a
        PioneerRx source or is left custom. Visibility is still filtered per role by field-level RBAC — a field or
        card a user can't see is hidden for them, and custom fields start hidden until granted on a role.
      </p>
      )}

      {error && <div style={errorBox}>{error}</div>}
      {notice && <div style={noticeBox}>{notice}</div>}
      {surface !== 'home' && (
      <ListColumnsPanel
        title={surface === 'rx' ? 'Pharmacy portal — Prescriptions list columns'
          : surface === 'patient' ? 'Pharmacy portal — Patients list columns'
          : surface === 'prescriber' ? 'Pharmacy portal — Prescribers list columns'
          : 'Home table columns'}
        // The generic title was the whole confusion: on the Prescriber view builder this panel
        // drives the pharmacy's PRESCRIBERS list, and nothing on screen said so — so edits here
        // were made while watching the provider portal, which reads a different surface entirely.
        // Every panel now names the screen it changes.
        hint={surface === 'rx' ? 'Columns on the pharmacy Prescriptions list (/rx). NOT the provider portal — that is the next panel down.'
          : surface === 'patient' ? 'Columns on the pharmacy Patients list (/patients).'
          : surface === 'prescriber' ? 'Columns on the pharmacy Prescribers list (/prescribers). The provider portal does not read this surface.'
          : "Columns shown on this surface's list screen for this pharmacy — reorder, show/hide, rename, repoint. Saved with the layout."}
        ls={pharmacyList} catalog={listCatalogGated} customSources={customSources} showProv={true} surface={surface} pioneerPaths={pioneerPaths} pharmacyId={pharmacyId} onMappingChanged={() => { mappingsDirty.current = true }} />
      )}
      {surface === 'rx' && (
        <ListColumnsPanel
          title="Provider portal — My Prescriptions columns"
          hint="Columns provider / provider-nurse users see on their My Prescriptions screen for this pharmacy. Independent of the pharmacy home table."
          ls={providerList} catalog={gateCat(PROVIDER_RX_CATALOG)} customSources={[]} showProv={false} surface={surface} pioneerPaths={pioneerPaths} pharmacyId={pharmacyId} onMappingChanged={() => { mappingsDirty.current = true }} />
      )}
      {surface === 'facility' && (
        <ListColumnsPanel
          title="Facility portal — My patients columns"
          hint="What an EXTERNAL facility user sees in their patients table. Same rules as the prescriptions table: a hidden column is not sent to the browser at all, and the list is a fixed safe set you can reorder and hide."
          ls={facPtList} catalog={gateCat(FAC_PT_CATALOG)} customSources={[]} showProv={false} surface={surface} pioneerPaths={pioneerPaths} pharmacyId={pharmacyId} onMappingChanged={() => { mappingsDirty.current = true }} />
      )}
      {surface === 'patient' && (
        <ListColumnsPanel
          title="Active prescriptions columns (pharmacy patient card)"
          hint="Columns of the Active Prescriptions table on the pharmacy patient card. SUB hides the small sub-text under a column."
          ls={patientRxList} catalog={gateCat(PATIENT_RX_CATALOG)} customSources={[]} showProv={false} surface={surface} pioneerPaths={pioneerPaths} pharmacyId={pharmacyId} onMappingChanged={() => { mappingsDirty.current = true }} />
      )}
      {surface === 'prescriber' && (
        <ListColumnsPanel
          title="Pharmacy portal — prescriber card, Active prescriptions columns"
          hint="Columns of the Active Prescriptions table on the pharmacy's prescriber detail page. Not a provider-portal surface."
          ls={prescRxList} catalog={gateCat(PRESC_RX_CATALOG)} customSources={[]} showProv={false} surface={surface} pioneerPaths={pioneerPaths} pharmacyId={pharmacyId} onMappingChanged={() => { mappingsDirty.current = true }} />
      )}
      {surface === 'facility' && (
        <ListColumnsPanel
          title="Facility portal — My prescriptions columns"
          hint="What an EXTERNAL facility user sees in their prescriptions table. A hidden column is not sent to the browser at all, not merely hidden. The list is a fixed safe set: you can reorder and hide, but not add fields the portal does not already return."
          ls={facRxList} catalog={gateCat(FAC_RX_CATALOG)} customSources={[]} showProv={false} surface={surface} pioneerPaths={pioneerPaths} pharmacyId={pharmacyId} onMappingChanged={() => { mappingsDirty.current = true }} />
      )}
      {surface === 'patient' && (
        <ListColumnsPanel
          title="Provider portal — patient card prescriptions columns"
          hint="Columns of the Prescriptions table providers see on their patient card. Fully independent of the pharmacy patient card: its own order, visibility, labels and SUB toggles."
          ls={providerPtRxList} catalog={gateCat(PROVIDER_PT_RX_CATALOG)} customSources={[]} showProv={false} surface={surface} pioneerPaths={pioneerPaths} pharmacyId={pharmacyId} onMappingChanged={() => { mappingsDirty.current = true }} />
      )}
      {loading && <div style={{ padding: 20, color: 'var(--color-text-muted)' }}>Loading…</div>}

      {!loading && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
          {/* Builder */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* ── HEADER region — fully composable, seeded from the current header ── */}
            <div style={{ marginBottom: 8 }}>
              <div style={regionLabel}><span style={{ color: '#0f766e' }}>HEADER</span> · the header every {surface} view shows</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {cards.map((c, ci) => ((c.config?.region ?? 'body') === 'header') ? renderCardRow(c, ci) : null)}
                {!hasHeaderCard ? (
                  <div style={cardBox}>
                    <div style={{ padding: 14, fontSize: 12.5, color: 'var(--color-text-muted)' }}>
                      The header currently shows the built-in identity band. Customize it to add, remove or reorder any field
                      (Status, DOB, …), relabel it, and map it to a PioneerRx source — just like the body.
                      <div style={{ marginTop: 10 }}>
                        <button onClick={seedHeaderFromCurrent} style={secondaryBtn}><Plus size={14} /> Customize header (start from current)</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => addRegionCard('header')} style={{ ...secondaryBtn, alignSelf: 'flex-start' }}><Plus size={14} /> Add header card</button>
                )}
              </div>
            </div>

            {/* ── BODY region ── */}
            <div style={{ marginBottom: 8 }}>
              <div style={regionLabel}><span style={{ color: 'var(--color-text-soft)' }}>BODY</span> · cards</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {cards.map((c, ci) => ((c.config?.region ?? 'body') === 'body') ? renderCardRow(c, ci) : null)}
                {!cards.some(c => (c.config?.region ?? 'body') === 'body') && (
                  <div style={{ color: 'var(--color-text-muted)', padding: 12, fontSize: 12.5 }}>No body cards. Use “Add card”.</div>
                )}
              </div>
            </div>

            {/* ── FOOTER region — fully composable ── */}
            <div>
              <div style={regionLabel}><span style={{ color: '#7c3aed' }}>FOOTER</span> · fields (optional)</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {cards.map((c, ci) => ((c.config?.region ?? 'body') === 'footer') ? renderCardRow(c, ci) : null)}
                {!cards.some(c => (c.config?.region ?? 'body') === 'footer') && (
                  <div style={{ color: 'var(--color-text-muted)', padding: 12, fontSize: 12.5 }}>No footer fields yet — add a footer card to compose them.</div>
                )}
                <button onClick={() => addRegionCard('footer')} style={{ ...secondaryBtn, alignSelf: 'flex-start' }}><Plus size={14} /> Add footer card</button>
              </div>
            </div>
          </div>

          {/* Live preview */}
          <div style={previewWrap}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--color-primary)', fontWeight: 700 }}>Live preview — {surface} detail view</span>
              <div style={{ flex: 1 }} />
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>View as</label>
              <select value={viewAsRole} onChange={e => setViewAsRole(e.target.value)} style={{ ...srcSelect, maxWidth: 200 }}>
                <option value="">Admin (sees everything)</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}{r.is_system ? ' (system)' : ''}</option>)}
              </select>
            </div>
            {viewAsRole ? (
              <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)', marginBottom: 8 }}>
                Sample data, shown as this role sees it: hidden fields/cards are dropped, masked fields are blanked (●).
              </div>
            ) : (
              <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)', marginBottom: 8 }}>
                Sample data (illustrative). Pick a role in “View as” to see masking/hiding applied.
              </div>
            )}
            {(() => {
              const shown = cards.filter(c => c.visible && !cardGatedOut(c))
              const inRegion = (r: 'header' | 'body' | 'footer') => shown.filter(c => cardRegion(c) === r)
              const Region = ({ label, list, cols }: { label: string; list: RxViewCard[]; cols: string }) => {
                const rendered = list.map(renderPvCard).filter(Boolean)
                if (rendered.length === 0) return null
                return (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--color-text-muted)', margin: '0 0 4px 2px' }}>{label}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 10 }}>{rendered}</div>
                  </div>
                )
              }
              return (
                <>
                  {inRegion('header').length === 0 && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--color-text-muted)', margin: '0 0 4px 2px' }}>Header</div>
                      <div style={pvMuted}>Built-in identity band (default). Customize the header to compose it.</div>
                    </div>
                  )}
                  <Region label="Header" list={inRegion('header')} cols="1fr" />
                  <Region label="Body" list={inRegion('body')} cols="1fr 1fr" />
                  <Region label="Footer" list={inRegion('footer')} cols="1fr" />
                </>
              )
            })()}
          </div>
        </div>
      )}
    </div>
  )
}

// Searchable PioneerRx source picker. Rendered in a portal so the card's
// overflow:hidden can't clip the dropdown.
function PathSelect({ value, paths, onChange, bindingLabel }: { value: string | null; paths: PioneerPath[]; onChange: (p: string) => void; bindingLabel?: string | null }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const btnRef = useRef<HTMLButtonElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  useEffect(() => {
    if (!open) return
    const place = () => {
      const r = btnRef.current?.getBoundingClientRect()
      if (r) setPos({ top: r.bottom + 4, left: Math.max(8, Math.min(r.left, window.innerWidth - 332)) })
    }
    place()
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => { document.removeEventListener('mousedown', onDoc); window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place) }
  }, [open])
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase()
    const base = s ? paths.filter(p => p.path.toLowerCase().includes(s) || p.section.toLowerCase().includes(s)) : paths
    return base.slice(0, 200)
  }, [q, paths])
  return (
    <div style={{ width: 230, flexShrink: 0 }}>
      <button ref={btnRef} type="button" onClick={() => setOpen(o => !o)} style={pathBtn}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: value ? undefined : 'var(--color-text-muted)' }}>{value || bindingLabel || '— Not mapped (custom) —'}</span>
        <ChevronDown size={13} style={{ flexShrink: 0 }} />
      </button>
      {open && createPortal(
        <div ref={popRef} style={{ ...pathPop, top: pos.top, left: pos.left }}>
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search PioneerRx fields…" style={pathSearch} />
          <div style={{ maxHeight: 260, overflowY: 'auto' }}>
            <div style={pathOpt} onMouseDown={() => { onChange(''); setOpen(false) }}><em>— Not mapped (custom) —</em></div>
            {filtered.map(p => (
              <div key={p.path} style={pathOpt} onMouseDown={() => { onChange(p.path); setOpen(false) }}>
                <div style={{ fontSize: 12 }}>{p.path}</div>
                <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>{p.section}</div>
              </div>
            ))}
            {!filtered.length && <div style={{ ...pathOpt, color: 'var(--color-text-muted)' }}>No matches{paths.length === 0 ? ' (no PioneerRx paths loaded)' : ''}</div>}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

// ---- styles ----
const badge: React.CSSProperties = { fontSize: 11, background: '#eef4fb', color: '#2563eb', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }
const labelInput: React.CSSProperties = { flex: 1, minWidth: 80, border: '1px solid transparent', borderRadius: 6, padding: '4px 6px', fontSize: 13, fontWeight: 600, background: 'transparent', color: 'var(--color-text)' }
const pathBtn: React.CSSProperties = { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, border: '1px solid var(--color-border)', borderRadius: 6, padding: '4px 8px', fontSize: 12, background: 'var(--color-bg)', cursor: 'pointer', color: 'var(--color-text)' }
const pathPop: React.CSSProperties = { position: 'fixed', width: 320, maxWidth: '90vw', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', zIndex: 1000, overflow: 'hidden' }
const pathSearch: React.CSSProperties = { width: '100%', border: 'none', borderBottom: '1px solid var(--color-border)', padding: '8px 10px', fontSize: 13, outline: 'none', background: 'var(--color-bg)' }
const pathOpt: React.CSSProperties = { padding: '6px 10px', cursor: 'pointer', borderBottom: '1px solid var(--color-border-subtle, #f1f4f7)' }
const selectStyle: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 7, padding: '5px 8px', fontSize: 13, background: 'var(--color-bg)', maxWidth: 260 }
const primaryBtn: React.CSSProperties = { border: '1px solid var(--color-primary)', background: 'var(--color-primary)', color: '#fff', borderRadius: 7, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const secondaryBtn: React.CSSProperties = { border: '1px solid var(--color-border)', background: 'var(--color-bg)', borderRadius: 7, padding: '6px 10px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }
const cardBox: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 9, overflow: 'hidden', background: 'var(--color-surface)' }
const regionLabel: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, letterSpacing: '0.07em', color: 'var(--color-text-muted)', textTransform: 'uppercase', margin: '0 0 6px 2px' }
const cardHead: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: 'var(--color-bg)', borderBottom: '1px solid var(--color-border)' }
const accessRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: '1px solid var(--color-border-subtle, #f1f4f7)', fontSize: 12 }
const roleChip: React.CSSProperties = { fontSize: 11, lineHeight: 1.2, padding: '3px 8px', borderRadius: 999, border: '1px solid var(--color-border, #d6dde5)', background: 'var(--color-surface, #fff)', color: 'var(--color-text-muted, #5b6770)', cursor: 'pointer', whiteSpace: 'nowrap' }
const roleChipOn: React.CSSProperties = { background: 'var(--color-primary, #2563eb)', borderColor: 'var(--color-primary, #2563eb)', color: '#fff', fontWeight: 600 }
const titleInput: React.CSSProperties = { flex: 1, border: '1px solid transparent', borderRadius: 6, padding: '3px 6px', fontSize: 13, fontWeight: 700, background: 'transparent', color: 'var(--color-text)' }
const fieldRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: '1px solid var(--color-border-subtle, #f1f4f7)' }
const specialRow: React.CSSProperties = { padding: '8px 10px', fontSize: 12.5, color: 'var(--color-text-muted)' }
const srcSelect: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 6, padding: '3px 6px', fontSize: 12, maxWidth: 220, background: 'var(--color-bg)' }
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'inline-flex', padding: 2 }
const miniBtn: React.CSSProperties = { background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: 0, lineHeight: 1 }
const miniTextBtn: React.CSSProperties = { background: 'transparent', border: '1px solid var(--color-border)', borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: 'var(--color-text)' }
const kindPill: React.CSSProperties = { fontSize: 10, background: '#eef2ff', color: '#3f4db8', padding: '2px 7px', borderRadius: 999, fontWeight: 700, textTransform: 'uppercase' }
const customPill: React.CSSProperties = { fontSize: 10, background: '#fef3c7', color: '#b45309', padding: '2px 7px', borderRadius: 999, fontWeight: 700 }
const defaultPill: React.CSSProperties = { fontSize: 10, background: '#eef4fb', color: '#2563eb', padding: '2px 7px', borderRadius: 999, fontWeight: 700 }
const standardPill: React.CSSProperties = { fontSize: 10, background: '#eefaf0', color: '#15803d', padding: '2px 7px', borderRadius: 999, fontWeight: 700 }
const autoPill: React.CSSProperties = { fontSize: 10, background: '#eef2ff', color: '#4338ca', padding: '2px 7px', borderRadius: 999, fontWeight: 700 }

// D-VIEWCOLS-1: home-table column catalogs per surface. `column` is the unit key the
// list screen understands; order here = default order; `visible:false` entries ship
// available-but-off (e.g. Date filled) until a pharmacy toggles them on.
const LIST_CATALOG: Record<string, { table: string; column: string; label: string; visible: boolean }[]> = {
  rx: [
    { table: 'rx_items', column: 'rx_number', label: 'RX #', visible: true },
    { table: 'patients', column: 'patient_name', label: 'Patient', visible: true },
    { table: 'rx_items', column: 'drug_name', label: 'Drug', visible: true },
    { table: 'rx_items', column: 'sig', label: 'Sig', visible: true },
    { table: 'rx_items', column: 'quantity_authorized', label: 'Quantity', visible: true },
    { table: 'prescribers', column: 'prescriber_name', label: 'Prescriber', visible: true },
    { table: 'rx_items', column: 'state', label: 'Status', visible: true },
    { table: 'rx_items', column: 'payer_state', label: 'Payer', visible: true },
    { table: 'rx_items', column: 'date_filled_utc', label: 'Date filled', visible: false },
    { table: 'rx_items', column: 'date_written', label: 'Date written', visible: false },
    // WorkBench operational columns (module-driven on the portal; listed here so the
    // admin sees and controls the FULL column set, matching the user column manager).
    { table: 'rx_workbench', column: 'wb_pharmacy_queue', label: 'Pharmacy Queue', visible: true },
    { table: 'rx_workbench', column: 'wb_task_type', label: 'Task Type', visible: true },
    { table: 'rx_workbench', column: 'wb_auth_status', label: 'Auth Status', visible: true },
    { table: 'rx_workbench', column: 'wb_order_status', label: 'Order Status', visible: true },
    { table: 'rx_workbench', column: 'wb_service_status', label: 'Service Status', visible: true },
    { table: 'rx_workbench', column: 'wb_welcome_call_status', label: 'Welcome Call', visible: true },
    { table: 'rx_workbench', column: 'wb_consultation_status', label: 'Consultation', visible: true },
    { table: 'rx_workbench', column: 'wb_delivery_call', label: 'Delivery Call', visible: true },
    { table: 'rx_workbench', column: 'wb_copay_collection', label: 'CoPay Collection', visible: true },
    // Canonical patient copay: the latest POSITIVE rx_claims.patient_pay_amount_paid across all claims
    // (identical logic to the shipping module + the printed label). Distinct from the Pioneer-path
    // "Co-Pay Amount" custom field, which reads only Claim[0] (the first claim, frequently $0). Map the
    // copay column to THIS to get the real paid amount. Resolved natively from the list's copay_amount.
    { table: 'rx_items', column: 'copay_amount', label: 'Co-Pay Amount (paid)', visible: false },
    { table: 'rx_workbench', column: 'wb_cpap_status', label: 'CPAP Status', visible: true },
    { table: 'rx_workbench', column: 'wb_biopsy', label: 'Biopsy', visible: true },
    { table: 'rx_workbench', column: 'wb_patient_category', label: 'Patient Category', visible: true },
    { table: 'rx_workbench', column: 'wb_copay_needed', label: 'Priority', visible: true },
    // RCA-ASP-66 alignment: the builder catalog must cover the portal's FULL registry —
    // these existed on the portal but were unmanageable from here.
    { table: 'rx_workbench', column: 'wb_delivery_status', label: 'Delivery Status', visible: false },
    { table: 'rx_workbench', column: 'wb_drug_category', label: 'Drug Category (workbench)', visible: false },
    { table: 'rx_items', column: 'pioneerrx_rx_status', label: 'PMS Rx Status', visible: false },
    { table: 'rx_items', column: 'priority_type_text', label: 'Priority (PMS)', visible: false },
    // CV-3: the patient comment fields are mapped from PioneerRx and exist on dbo.patients,
    // but were absent from BOTH catalogs — so an admin could not surface them as a column and
    // a pharmacy could not filter on them. Catalog side added in migration 320. Default hidden:
    // these are free-text clinical notes (sensitivity 'phi'), opt-in per pharmacy.
    { table: 'patients', column: 'comment_critical', label: 'Patient critical comments', visible: false },
    { table: 'patients', column: 'comment_informational', label: 'Patient info comments', visible: false },
    { table: 'rx_items', column: 'current_tx_status_text', label: 'Pharmacy Queue (PMS)', visible: false },
    { table: 'rx_items', column: 'pioneerrx_tx_status', label: 'PMS TX Status', visible: false },
    { table: 'rx_items', column: 'directions_translated', label: 'Translated Directions', visible: false },
    // D-DRUGREF-3b: DrugReference columns (module-gated in the builder; resolved via batch endpoint)
    { table: 'drug_reference', column: 'category', label: 'Drug category', visible: false },
    { table: 'drug_reference', column: 'rems_program', label: 'REMS program', visible: false },
    { table: 'drug_reference', column: 'copay_program', label: 'Copay program', visible: false },
    { table: 'rx_items', column: 'last_state_change_at', label: 'PMS Last Update', visible: true },
    // Workbench-owned change stamp (rx_workbench.updated_at / updated_by) — distinct from the PMS
    // last-change above. These were MISSING from the picker, so a pharmacy wanting "Workbench Last
    // Update" could only pick the PMS field by mistake. wb_updated_at resolves to the base column
    // that reads rx_workbench.updated_at.
    { table: 'rx_workbench', column: 'wb_updated_at', label: 'Workbench Last Update', visible: true },
    { table: 'rx_workbench', column: 'wb_updated_by', label: 'Workbench Updated By', visible: false },
    // ASP-64 / ClickUp 86cb27v09: the WorkBench manual due date as a list column. Hidden by
    // default — a pharmacy opts in per tab via the rx_list:<tabKey> override. Display only on the
    // list; the value is still edited on the Rx-detail WorkBench panel.
    { table: 'rx_workbench', column: 'wb_task_due_date', label: 'Task Due Date', visible: false },
    { table: 'rx_items', column: 'days_in_queue', label: 'Days in queue', visible: false },   // ASP-69 derived (created_at anchor)
    // D-SOC-2 (Patrick 2026-09-04): site of care — residential or the facility type, and the facility name.
    { table: 'rx_site_of_care', column: 'site_of_care_type', label: 'Site of care', visible: true },
    { table: 'rx_site_of_care', column: 'site_of_care_facility', label: 'Facility', visible: true },
  ],
  patient: [
    { table: 'patients', column: 'patient', label: 'Patient', visible: true },
    { table: 'patients', column: 'date_of_birth', label: 'DOB', visible: true },
    { table: 'patients', column: 'contact', label: 'Contact', visible: true },
    { table: 'patients', column: 'insurance', label: 'Insurance', visible: true },
    { table: 'patients', column: 'allergies', label: 'Allergies', visible: true },
    { table: 'patients', column: 'rx', label: 'Rx', visible: true },
    { table: 'patients', column: 'last_activity', label: 'Last activity', visible: true },
    // D-SOC-2
    { table: 'patient_site_of_care', column: 'site_of_care', label: 'Site of care', visible: true },
    { table: 'patient_site_of_care', column: 'site_of_care_facility', label: 'Facility', visible: true },
  ],
  prescriber: [
    { table: 'prescribers', column: 'prescriber', label: 'Prescriber', visible: true },
    { table: 'prescribers', column: 'npi_dea', label: 'NPI / DEA', visible: true },
    { table: 'prescribers', column: 'specialty', label: 'Specialty', visible: true },
    { table: 'prescribers', column: 'clinic', label: 'Clinic', visible: true },
    { table: 'prescribers', column: 'contact', label: 'Contact', visible: true },
    { table: 'prescribers', column: 'rx', label: 'Rx written', visible: true },
    { table: 'prescribers', column: 'last_activity', label: 'Last activity', visible: true },
  ],
}

// Builder column names that drift from the canonical DefaultMappings column names.
const FIELD_ALIAS: Record<string, string> = {
  'patients.gender': 'sex',
  'rx_items.day_supply': 'days_supply',
  'rx_items.origin_type': 'origin_type_text',
  'rx_items.drug_use_type': 'drug_use_type_text',
  'rx_items.data_entry_employee_id': 'data_entry_by_pioneerrx_id',
  'rx_items.pharmacist_employee_id': 'pharmacist_pioneerrx_id',
  'rx_items.checked_by_pharmacist_employee_id': 'checked_by_pharmacist_pioneerrx_id',
}
// Fields composed from several PioneerRx fields (no single canonical path) — honest source descriptor.
const FIELD_COMPOSITE: Record<string, string> = {
  'patients.display_name': 'Body.Patient.Name.FirstName + LastName',
  'patients.address': 'Body.Patient.Addresses.Address[] (line/city/state/zip)',
  'prescribers.display_name': 'Body.Prescribers.Prescriber[].Name.FirstName + LastName',
  'prescribers.address': 'Body.Prescribers.Prescriber[].Addresses.Address[] (line/city/state/zip)',
  'prescribers.prescriber': 'Body.Prescribers.Prescriber[].Name.FirstName + LastName (linked prescriber)',
}
// Resolve a non-custom builder field to its canonical PioneerRx source (path or composite descriptor).
function canonicalPath(fieldDefaults: Record<string, string>, f: { owlrx_table: string; owlrx_column: string }): string | null {
  if (f.owlrx_table === 'custom') return null
  const key = `${f.owlrx_table}.${f.owlrx_column}`
  if (fieldDefaults[key]) return fieldDefaults[key]
  const alias = FIELD_ALIAS[key]
  if (alias && fieldDefaults[`${f.owlrx_table}.${alias}`]) return fieldDefaults[`${f.owlrx_table}.${alias}`]
  if (FIELD_COMPOSITE[key]) return FIELD_COMPOSITE[key]
  return null
}
const previewWrap: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 9, padding: 14, background: 'var(--color-surface)', position: 'sticky', top: 8 }
const pvCard: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 8, padding: '9px 10px', background: 'var(--color-bg)' }
const pvHead: React.CSSProperties = { fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--color-text-muted)', marginBottom: 6, fontWeight: 700 }
const pvField: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: 8, padding: '2px 0', fontSize: 12, borderBottom: '1px dashed var(--color-border-subtle, #eef1f5)' }
const pvMuted: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-muted)' }
const errorBox: React.CSSProperties = { background: '#fde8e8', border: '1px solid #f5c2c2', color: '#b3261e', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 10 }
const noticeBox: React.CSSProperties = { background: '#eef4fb', border: '1px solid #d6e4f5', color: '#33404f', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 10 }

// ---------------------------------------------------------------------------
// D-VIEWCOLS-3: audience-scoped list-column surfaces
// ---------------------------------------------------------------------------
const PATIENT_RX_CATALOG: { table: string; column: string; label: string; visible: boolean }[] = [
  { table: 'rx_items',   column: 'rx_number',           label: 'Rx #',          visible: true },
  { table: 'rx_items',   column: 'drug_name',           label: 'Drug',          visible: true },
  { table: 'rx_items',   column: 'sig',                 label: 'Sig',           visible: true },
  { table: 'rx_items',   column: 'quantity_authorized', label: 'Quantity',      visible: true },
  { table: 'prescribers',column: 'prescriber_name',     label: 'Prescriber',    visible: true },
  { table: 'rx_items',   column: 'state',               label: 'Clinical',      visible: true },
  { table: 'rx_items',   column: 'payer_state',         label: 'Payer',         visible: true },
  { table: 'rx_items',   column: 'pioneerrx_rx_status',  label: 'PMS Rx Status',  visible: false },
  { table: 'rx_items',   column: 'pioneerrx_tx_status',  label: 'PMS TX Status',  visible: false },
  { table: 'rx_items',   column: 'directions_translated', label: 'Translated Directions', visible: false },
  { table: 'rx_items',   column: 'last_activity',       label: 'Last activity', visible: true },
  { table: 'drug_reference', column: 'category', label: 'Drug category', visible: false },
  { table: 'drug_reference', column: 'rems_program', label: 'REMS program', visible: false },
  { table: 'drug_reference', column: 'copay_program', label: 'Copay program', visible: false },
  // D-SOC-3
  { table: 'rx_site_of_care', column: 'site_of_care_type', label: 'Site of care', visible: true },
  { table: 'rx_site_of_care', column: 'site_of_care_facility', label: 'Facility', visible: true },
]

// Prescriber card — Active Prescriptions (surface: presc_rx_list; <=16 chars fits
// pharmacy_view_fields.surface NVARCHAR(16)). BA-31.
// D-SOC-8 — the FACILITY portal's prescription table (surface: fac_rx_list).
//
// DELIBERATELY NARROWER than PRESC_RX_CATALOG. Every other builder configures what PHARMACY STAFF
// see; this one configures what an EXTERNAL party sees, so the catalogue is a security boundary
// rather than a preference. It lists only fields the portal endpoint already projects through its
// explicit allow-list, so an admin can reorder and hide but can never ADD a field the server does
// not return — ticking a box here cannot widen what leaves the building.
//
// Notably ABSENT, and on purpose: sig, payer state, PMS statuses, translated directions, and the
// drug-reference fields. Those are clinical/operational detail for staff, not for a facility.
//
// rx_number is not listed because it is never hideable — without it an operator cannot tell two
// rows apart or quote one to the pharmacy.
// D-SOC-9 S3 — the FACILITY portal's "My patients" table. Deliberately narrow, like
// FAC_RX_CATALOG: a contracted SNF is an external party, so this is a fixed safe set an admin can
// reorder and hide, never a door onto every patient column. Every entry here MUST have a renderer
// in ProviderDashboard's patientCols — columnRendererParity.test.ts fails the build otherwise,
// because a catalog column with no renderer silently does nothing (the bug Mario hit five times).
// D-SOC-9 — the FACILITY portal's Home tiles. NOT the pharmacy HOME_SECTION_CATALOG: that one is
// pharmacy-operations (prior auth, holds over 24h, rejections, pre-check aging) and means nothing
// to a nursing home. These are the numbers facility/summary actually returns, so every card here
// has real data behind it.
const FAC_HOME_CATALOG: { key: string; title: string; metric: string; body: string }[] = [
  { key: 'active_rx',       title: 'Active prescriptions', metric: 'COUNT of Rx for your residents that are not cancelled / expired / completed', body: 'Links to My prescriptions.' },
  { key: 'active_patients', title: 'Active residents',     metric: 'COUNT DISTINCT residents with a live prescription', body: 'Links to My patients.' },
  { key: 'needs_attention', title: 'Needs attention',      metric: 'COUNT of Rx on hold or awaiting authorization', body: 'Links to My prescriptions.' },
  { key: 'unread',          title: 'Unread messages',      metric: 'COUNT of unread notifications for this account', body: 'Links to the inbox.' },
  { key: 'recent_rx',       title: 'Recent prescriptions', metric: 'No metric — the six most recent Rx', body: 'A strip of recent prescriptions, each linking to the list.' },
]

const FAC_PT_CATALOG: { table: string; column: string; label: string; visible: boolean }[] = [
  { table: 'patients', column: 'patient_name',  label: 'Resident',      visible: true },
  { table: 'patients', column: 'mrn',           label: 'MRN',           visible: false },
  { table: 'patients', column: 'date_of_birth', label: 'DOB',           visible: false },
  { table: 'rx_items', column: 'rx_count',      label: 'Prescriptions', visible: true },
  { table: 'rx_items', column: 'last_rx_at',    label: 'Last activity', visible: true },
  // D-SOC-10 B-3 (Patrick 2026-09-25): "MY Patients missing the facility column and no way in the
  // Admin portal to add it". Both halves were missing — this entry, and the projection in
  // facility/patients. Either alone reproduces the blank-column bug.
  { table: 'rx_site_of_care', column: 'facility_names', label: 'Facility', visible: true },
]

const FAC_RX_CATALOG: { table: string; column: string; label: string; visible: boolean }[] = [
  // 2026-09-28: the note above said Rx # "is never hideable", and the server does always return
  // it — but with no entry here and none in the portal's defaults, the facility table never
  // SHOWED it. A pharmacy had to add it as a PioneerRx-mapped column, which reads the extract
  // store and is blank until re-ingest. It is a native column now, first by default.
  { table: 'rx_items',        column: 'rx_number',           label: 'Rx #',          visible: true },
  { table: 'patients',        column: 'patient_name',        label: 'Resident',      visible: true },
  { table: 'rx_items',        column: 'drug_name',           label: 'Medication',    visible: true },
  { table: 'rx_items',        column: 'drug_strength',       label: 'Strength',      visible: true },
  { table: 'prescribers',     column: 'prescriber_name',     label: 'Prescriber',    visible: true },
  { table: 'rx_items',        column: 'quantity_authorized', label: 'Quantity',      visible: true },
  { table: 'rx_items',        column: 'days_supply',         label: 'Days supply',   visible: true },
  { table: 'rx_items',        column: 'state',               label: 'Rx status',     visible: true },
  { table: 'rx_shipments',    column: 'ship_status',         label: 'Delivery',      visible: true },
  { table: 'rx_shipments',    column: 'ship_tracking',       label: 'Tracking',      visible: true },
  { table: 'rx_site_of_care', column: 'facility_name',       label: 'Facility',      visible: true },
  { table: 'rx_items',        column: 'last_activity',       label: 'Last activity', visible: true },
]

const PRESC_RX_CATALOG: { table: string; column: string; label: string; visible: boolean }[] = [
  { table: 'rx_items',   column: 'rx_number',           label: 'Rx #',          visible: true },
  { table: 'patients',   column: 'patient_name',        label: 'Patient',       visible: true },
  { table: 'rx_items',   column: 'drug_name',           label: 'Drug',          visible: true },
  { table: 'rx_items',   column: 'sig',                 label: 'Sig',           visible: true },
  { table: 'rx_items',   column: 'quantity_authorized', label: 'Quantity',      visible: true },
  { table: 'rx_items',   column: 'state',               label: 'Clinical',      visible: true },
  { table: 'rx_items',   column: 'payer_state',         label: 'Payer',         visible: true },
  { table: 'rx_items',   column: 'pioneerrx_rx_status',  label: 'PMS Rx Status',  visible: false },
  { table: 'rx_items',   column: 'pioneerrx_tx_status',  label: 'PMS TX Status',  visible: false },
  { table: 'rx_items',   column: 'directions_translated', label: 'Translated Directions', visible: false },
  { table: 'rx_items',   column: 'last_activity',       label: 'Last activity', visible: true },
  { table: 'drug_reference', column: 'category', label: 'Drug category', visible: false },
  { table: 'drug_reference', column: 'rems_program', label: 'REMS program', visible: false },
  { table: 'drug_reference', column: 'copay_program', label: 'Copay program', visible: false },
  // D-SOC-3
  { table: 'rx_site_of_care', column: 'site_of_care_type', label: 'Site of care', visible: true },
  { table: 'rx_site_of_care', column: 'site_of_care_facility', label: 'Facility', visible: true },
]

// Provider patient card — Active Prescriptions (surface: prov_pt_rx_list; name kept <=16 chars
// to fit pharmacy_view_fields.surface NVARCHAR(16)). Independent of the pharmacy patient card.
const PROVIDER_PT_RX_CATALOG: { table: string; column: string; label: string; visible: boolean }[] = [
  { table: 'rx_items',   column: 'rx_number',           label: 'Rx #',          visible: true },
  { table: 'rx_items',   column: 'drug_name',           label: 'Drug',          visible: true },
  { table: 'rx_items',   column: 'sig',                 label: 'Sig',           visible: true },
  { table: 'rx_items',   column: 'quantity_authorized', label: 'Quantity',      visible: true },
  { table: 'prescribers',column: 'prescriber_name',     label: 'Prescriber',    visible: false },
  { table: 'rx_items',   column: 'state',               label: 'Clinical',      visible: true },
  { table: 'rx_items',   column: 'payer_state',         label: 'Payer',         visible: false },
  { table: 'rx_items',   column: 'pioneerrx_rx_status',  label: 'PMS Rx Status',  visible: false },
  { table: 'rx_items',   column: 'pioneerrx_tx_status',  label: 'PMS TX Status',  visible: false },
  { table: 'rx_items',   column: 'directions_translated', label: 'Translated Directions', visible: false },
  { table: 'rx_items',   column: 'last_activity',       label: 'Last activity', visible: true },
  { table: 'drug_reference', column: 'category', label: 'Drug category', visible: false },
  { table: 'drug_reference', column: 'rems_program', label: 'REMS program', visible: false },
  { table: 'drug_reference', column: 'copay_program', label: 'Copay program', visible: false },
  // D-SOC-3
  { table: 'rx_site_of_care', column: 'site_of_care_type', label: 'Site of care', visible: true },
  { table: 'rx_site_of_care', column: 'site_of_care_facility', label: 'Facility', visible: true },
]

const PROVIDER_RX_CATALOG: { table: string; column: string; label: string; visible: boolean }[] = [
  { table: 'rx_items', column: 'rx_number', label: 'RX #', visible: true },
  { table: 'patients', column: 'patient_name', label: 'Patient', visible: true },
  { table: 'patients', column: 'patient_dob', label: 'DOB', visible: false },
  { table: 'rx_items', column: 'drug_name', label: 'Drug', visible: true },
  { table: 'rx_items', column: 'sig', label: 'Sig', visible: true },
  { table: 'rx_items', column: 'quantity_authorized', label: 'Quantity', visible: true },
  { table: 'rx_items', column: 'refill_number', label: 'Fill #', visible: false },
  // Native prescriber column: /provider/rx already joins prescribers.display_name onto every row.
  // Absent from this catalog, admins could only add it as a PioneerRx-mapped custom column, which
  // reads the sparse ingest extraction store and rendered blank for most Rx.
  { table: 'prescribers', column: 'prescriber_name', label: 'Prescriber', visible: false },
  { table: 'rx_items', column: 'state', label: 'Clinical', visible: true },
  { table: 'rx_items', column: 'pioneerrx_rx_status', label: 'PMS Rx Status', visible: false },
  { table: 'rx_items', column: 'pioneerrx_tx_status', label: 'PMS TX Status', visible: false },
  { table: 'rx_items', column: 'directions_translated', label: 'Translated Directions', visible: false },
  { table: 'rx_items', column: 'payer_state', label: 'Payer', visible: true },
  { table: 'rx_comments', column: 'comments', label: 'Comments', visible: true },
  { table: 'rx_items', column: 'last_activity', label: 'Last activity', visible: true },
  { table: 'drug_reference', column: 'category', label: 'Drug category', visible: false },
  { table: 'drug_reference', column: 'rems_program', label: 'REMS program', visible: false },
  { table: 'drug_reference', column: 'copay_program', label: 'Copay program', visible: false },
  // D-SOC-3
  { table: 'rx_site_of_care', column: 'site_of_care_type', label: 'Site of care', visible: true },
  { table: 'rx_site_of_care', column: 'site_of_care_facility', label: 'Facility', visible: true },
]

const listKeyOf = (f: any) => f.owlrx_table === 'custom' ? `cf:${f.owlrx_column}` : String(f.owlrx_column)

function useListSurface(
  pharmacyId: string, surfaceName: string,
  catalog: { table: string; column: string; label: string; visible: boolean }[],
  cards: RxViewCard[], appendCustoms: boolean,
  seedFromSurface?: string,
) {
  const [saved, setSaved] = useState<any[] | null>(null)
  const [fields, setFields] = useState<any[] | null>(null)
  const [provHidden, setProvHidden] = useState<Set<string>>(new Set())
  const [subHidden, setSubHidden] = useState<Set<string>>(new Set())
  useEffect(() => {
    if (!pharmacyId || !surfaceName) { setSaved(null); setFields(null); return }
    let dead = false
    setSaved(null); setFields(null)
    api.getRxViewLayout(pharmacyId, surfaceName).then(async l => {
      if (dead) return
      let card = ((l.cards ?? []) as any[]).find(c => c.card_key === 'list_columns')
      // D-TABCOLS-1 read-first (§5): if this is a per-tab surface with no saved override yet, seed the
      // editor from the pharmacy's shared columns (a SNAPSHOT — once saved the override is independent
      // and will not auto-follow later shared-column changes). Provider/subtext exclusions carry too.
      if (seedFromSurface && !(card?.fields?.length)) {
        try {
          const base = await api.getRxViewLayout(pharmacyId, seedFromSurface)
          if (dead) return
          const baseCard = ((base.cards ?? []) as any[]).find(c => c.card_key === 'list_columns')
          if (baseCard?.fields?.length) card = baseCard
        } catch { /* keep default shell */ }
      }
      setSaved(card?.fields ?? [])
      setProvHidden(new Set((((card?.config as any)?.provider_hidden ?? []) as string[])))
      setSubHidden(new Set((((card?.config as any)?.subtext_hidden ?? []) as string[])))
      setRemoved(new Set((((card?.config as any)?.removed ?? []) as string[])))
    }).catch(() => { if (!dead) setSaved([]) })
    return () => { dead = true }
  }, [pharmacyId, surfaceName, seedFromSurface])
  // Self-healing reconcile: saved rows keep order/labels/visibility; missing catalog
  // entries + (optionally) the pharmacy's custom fields are appended.
  // FIX (2026-07-02, "Saved but reverted"): rebuild ONLY when the server snapshot changes.
  // Previously this also re-ran when `cards` changed — and save() reloads cards — so every
  // Save rebuilt the rows from the PAGE-LOAD snapshot, visually reverting the user's edits;
  // the next Save then round-tripped that stale rebuild to the server.
  // Delete means GONE (Patrick 2026-07-22): keys the admin explicitly deleted; persisted in
  // the card config so the discoverability re-append can never resurrect them. Re-adding via
  // 'Add column' or the source picker clears the suppression.
  const [removed, setRemoved] = useState<Set<string>>(new Set())
  const builtFor = useRef<any>(null)
  useEffect(() => {
    if (saved === null || cards.length === 0 || !surfaceName) return
    if (builtFor.current === saved && fields) return
    builtFor.current = saved
    const rows: any[] = saved.map(f => ({ ...f }))
    const present = new Set(rows.map(listKeyOf))
    for (const c of catalog) {
      if (present.has(c.column)) continue
      if (removed.has(`${c.table}.${c.column}`)) continue   // admin deleted it — stays gone
      present.add(c.column)
      // Removal must stick (fix 2026-07-14): when a saved config exists, resurfaced catalog
      // columns come back HIDDEN — previously wb_* resurfaced VISIBLE, making workbench-column
      // removal impossible (save worked server-side; the UI resurrected the row and re-saved it).
      rows.push({ owlrx_table: c.table, owlrx_column: c.column, owlrx_label: c.label, pioneer_path: null, visible: saved.length ? false : c.visible, custom: false, data_type: 'text' })
    }
    if (appendCustoms) {
      const customs = cards.flatMap(c => (c.fields ?? []).filter((f: any) => f.owlrx_table === 'custom'))
      for (const f of customs) {
        const k = `cf:${f.owlrx_column}`
        if (present.has(k)) continue
        if (removed.has(`custom.${f.owlrx_column}`)) continue   // admin deleted it — stays gone
        present.add(k)
        // Same removal-must-stick rule for custom columns (previously re-appended visible:true
        // unconditionally, so removing a custom/Pioneer-mapped column could never persist in the UI).
        rows.push({ owlrx_table: 'custom', owlrx_column: f.owlrx_column, owlrx_label: f.owlrx_label, pioneer_path: f.pioneer_path ?? null, visible: saved.length ? false : true, custom: true, data_type: f.data_type ?? 'text' })
      }
    }
    // RCA-ASP-66 F2: visible rows first (portal order), hidden rows grouped after — the
    // panel's top block mirrors the portals 1:1 and the Available divider is one clean group.
    rows.sort((a, b) => Number(b.visible) - Number(a.visible))
    setFields(rows)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved, cards, surfaceName, removed])
  const persist = async () => {
    if (!surfaceName || !fields || !fields.length) return
    await api.putRxViewLayout([{
      card_key: 'list_columns', title: 'Home table columns', kind: 'fields', position: 0, visible: true,
      config: { provider_hidden: Array.from(provHidden), subtext_hidden: Array.from(subHidden), removed: Array.from(removed) },
      // Dedupe by (table, column) — duplicates violated UK_viewfield server-side and rolled
      // back the entire save (first occurrence wins, matching the server's guard).
      fields: fields.filter((f, k) => fields.findIndex(x => x.owlrx_table === f.owlrx_table && x.owlrx_column === f.owlrx_column) === k)
        .map((f, k) => ({ ...f, card_position: k })),
    } as any], pharmacyId, surfaceName)
    // Snapshot what was just persisted as the new server truth, so any later reconcile
    // rebuilds from the user's saved state — never from the page-load snapshot.
    setSaved(fields.map(f => ({ ...f })))
  }
  return { fields, setFields, provHidden, setProvHidden, subHidden, setSubHidden, persist, removed, setRemoved }
}

function ListColumnsPanel({ title, hint, ls, catalog, customSources, showProv, surface, pioneerPaths, pharmacyId, onMappingChanged }: {
  title: string; hint: string
  ls: ReturnType<typeof useListSurface>
  catalog: { table: string; column: string; label: string; visible: boolean }[]
  customSources: any[]
  showProv: boolean
  surface: string
  pioneerPaths?: api.PioneerPath[]
  pharmacyId?: string
  onMappingChanged?: () => void
}) {
  const { fields, setFields, provHidden, setProvHidden, subHidden, setSubHidden } = ls
  if (!fields || fields.length === 0) return null
  const move = (i: number, d: number) => setFields(fs => {
    if (!fs) return fs
    const n = [...fs]; const j = i + d
    if (j < 0 || j >= n.length) return fs
    ;[n[i], n[j]] = [n[j], n[i]]; return n
  })
  // Drag-to-reorder (ASP ColumnManager pattern). Keyed by table.column; arrows kept for a11y.
  const keyOf = (f: any) => `${f.owlrx_table}.${f.owlrx_column}`
  const { isDragging, rowProps, handleProps } = usePointerReorder((from, to) =>
    setFields(fs => fs ? reorderByKey(fs, keyOf, from, to) : fs))
  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 9, padding: 14, background: 'var(--color-surface)', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
        <h2 style={{ fontSize: 14, margin: 0 }}>{title}</h2>
        <span style={{ fontSize: 11.5, color: 'var(--color-text-muted)' }}>{hint}</span>
      </div>
      {fields.map((f, i) => (<>
        {/* RCA-ASP-66 F2: first hidden row starts the separated "Available" group so the top
            block reads EXACTLY as the portals render — visible columns, in order, nothing else. */}
        {(!f.visible && (i === 0 || fields[i - 1].visible)) && (
          <div key={`avail-${i}`} style={{ margin: '10px 0 4px', paddingTop: 8, borderTop: '1px dashed var(--color-border)', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: 'var(--color-text-muted)' }}>
            AVAILABLE (hidden — not on the portal)
          </div>
        )}
        <div key={`lc-${f.owlrx_table}-${f.owlrx_column}-${i}`} {...rowProps(keyOf(f))} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', opacity: f.visible ? 1 : 0.45, outline: isDragging(keyOf(f)) ? '1.5px solid var(--color-accent, #0f766e)' : 'none', borderRadius: 4, background: isDragging(keyOf(f)) ? 'rgba(15,118,110,0.08)' : 'transparent' }}>
          <span {...handleProps(keyOf(f))}><GripVertical size={12} style={{ color: 'var(--color-text-mute)' }} /></span>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <button onClick={() => move(i, -1)} disabled={i === 0} style={miniBtn} aria-label="Move column up"><ChevronUp size={11} /></button>
            <button onClick={() => move(i, 1)} disabled={i === fields.length - 1} style={miniBtn} aria-label="Move column down"><ChevronDown size={11} /></button>
          </div>
          <button onClick={() => setFields(fs => fs ? fs.map((x, k) => k === i ? { ...x, visible: !x.visible } : x) : fs)} style={iconBtn} title={f.visible ? 'Hide column' : 'Show column'}>
            {f.visible ? <Eye size={14} /> : <EyeOff size={14} />}
          </button>
          <input value={f.owlrx_label} onChange={e => setFields(fs => fs ? fs.map((x, k) => k === i ? { ...x, owlrx_label: e.target.value } : x) : fs)} style={labelInput} />
          <SearchableSelect
            value={f.custom && !customSources.some((c: any) => c.owlrx_column === f.owlrx_column)
              ? (f.pioneer_path ? `pio:${f.pioneer_path}` : `custom.${f.owlrx_column}`)
              : `${f.owlrx_table}.${f.owlrx_column}`}
            ariaLabel="Column source"
            style={{ flex: 1, minWidth: 220, maxWidth: 360, fontSize: 11 }}
            options={[
              ...catalog.map(c => ({ value: `${c.table}.${c.column}`, label: `${c.label} — ${c.table}.${c.column}` })),
              ...customSources.map((c: any) => ({ value: `custom.${c.owlrx_column}`, label: `Custom: ${(c.owlrx_label || '').trim() || c.owlrx_column}` })),
              // The row's own just-created pioneer column (not yet in customSources) stays selectable.
              ...(f.custom && f.pioneer_path && !customSources.some((c: any) => c.owlrx_column === f.owlrx_column)
                ? [{ value: `pio:${f.pioneer_path}`, label: `PioneerRx: ${f.pioneer_path}` }] : []),
              ...(pioneerPaths ?? []).filter(p => !(f.custom && f.pioneer_path === p.path)).map(p => ({ value: `pio:${p.path}`, label: `PioneerRx: ${p.path}` })),
            ]}
            onChange={async v => {
              if (v.startsWith('pio:')) {
                // D-VIEWCOLS-4 phase 2: picking a PioneerRx path creates a mapped custom column with
                // card_key='list_columns' — ingest extraction populates it from the next envelope on.
                const path = v.slice(4)
                const tail = path.split('.').pop()!.replace(/\[\d+\]/g, '')
                const col = ('pio_' + tail.replace(/[^A-Za-z0-9]/g, '_')).toLowerCase().slice(0, 60)
                const label = tail.replace(/([a-z])([A-Z])/g, '$1 $2')
                try {
                  await api.createFieldMapping({ owlrx_table: 'custom', owlrx_column: col, owlrx_label: label, pioneer_path: path, card_key: 'list_columns' }, pharmacyId)
                } catch (e: any) {
                  // mapping_exists (409) -> reuse the existing column key; anything else surfaces.
                  if (!String(e?.message ?? e).includes('mapping_exists')) { alert('Could not create the PioneerRx column: ' + (e?.message ?? e)); return }
                }
                onMappingChanged?.()
                setFields(fs => fs ? fs.map((x, k) => k === i
                  ? { ...x, owlrx_table: 'custom', owlrx_column: col, owlrx_label: x.owlrx_label?.trim() ? x.owlrx_label : label, custom: true, pioneer_path: path, data_type: 'text' }
                  : x) : fs)
                return
              }
              setFields(fs => fs ? fs.map((x, k) => {
                if (k !== i) return x
                if (v.startsWith('custom.')) {
                  const col = v.slice(7)
                  const src = customSources.find((c: any) => c.owlrx_column === col)
                  return { ...x, owlrx_table: 'custom', owlrx_column: col, custom: true, pioneer_path: src?.pioneer_path ?? null, data_type: src?.data_type ?? 'text' }
                }
                const dot = v.indexOf('.')
                return { ...x, owlrx_table: v.slice(0, dot), owlrx_column: v.slice(dot + 1), custom: false, pioneer_path: null }
              }) : fs)
            }}
          />
          {showProv && (
            <button
              onClick={() => setProvHidden(s => { const n = new Set(s); const k = listKeyOf(f); n.has(k) ? n.delete(k) : n.add(k); return n })}
              title={provHidden.has(listKeyOf(f)) ? 'Hidden from provider view — click to show' : 'Shown in provider view — click to hide'}
              style={{
                fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 999, cursor: 'pointer',
                border: '1px solid var(--color-border)',
                background: provHidden.has(listKeyOf(f)) ? '#fef2f2' : '#f0fdf4',
                color: provHidden.has(listKeyOf(f)) ? '#991b1b' : '#166534',
                textDecoration: provHidden.has(listKeyOf(f)) ? 'line-through' : 'none',
              }}
            >PROV</button>
          )}
          <button
            onClick={() => setSubHidden(s => { const n = new Set(s); const k = listKeyOf(f); n.has(k) ? n.delete(k) : n.add(k); return n })}
            title={subHidden.has(listKeyOf(f)) ? 'Sub-text hidden — click to show' : 'Sub-text shown — click to hide the second line'}
            style={{
              fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 999, cursor: 'pointer',
              border: '1px solid var(--color-border)',
              background: subHidden.has(listKeyOf(f)) ? '#fef2f2' : '#f8fafc',
              color: subHidden.has(listKeyOf(f)) ? '#991b1b' : '#475569',
              textDecoration: subHidden.has(listKeyOf(f)) ? 'line-through' : 'none',
            }}
          >SUB</button>
          <button
            onClick={async () => {
              // RCA-ASP-66 F3: deleting a CUSTOM column also deletes its mapping row — deleted
              // means GONE (no dimmed resurrection, no orphaned duplicate haunting the picker).
              if (f.custom) {
                const src = customSources.find((c: any) => c.owlrx_column === f.owlrx_column)
                if (src?.id && window.confirm(`Delete the custom column "${(f.owlrx_label || f.owlrx_column)}" and its mapping permanently?`)) {
                  try { await api.deleteFieldMapping(src.id, pharmacyId) } catch { alert('Could not delete the mapping — the column row was still removed from this list.') }
                }
              }
              // Delete means GONE: suppress this key so the discoverability re-append can
              // never resurrect it (persisted with the layout on Save).
              ls.setRemoved((r: Set<string>) => new Set(r).add(f.custom ? `custom.${f.owlrx_column}` : `${f.owlrx_table}.${f.owlrx_column}`))
              setFields(fs => fs ? fs.filter((_, k) => k !== i) : fs)
              onMappingChanged?.()
            }}
            title={f.custom ? 'Delete this custom column and its mapping permanently' : 'Delete this column from this pharmacy (re-add later via Add column)'}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: 2 }}
          ><Trash2 size={13} /></button>
        </div>
        {/* COMBINE FIELDS - any number of PioneerRx properties, on ANY column (owner 2026-09-03:
            "i cannot combine any more props together" / "no to any col"). The first cut of this
            offered exactly ONE extra field and only on columns already PioneerRx-mapped, which is
            neither of the things asked for. The backend never had that limit - the stored path is
            split on '+' and joined with spaces for as many parts as are present - so this is purely
            the editor catching up: parts are chips you add to and remove from, and picking the first
            one ADOPTS a plain column as a PioneerRx column exactly as the source dropdown does. */}
        {(() => {
          const parts: string[] = f.pioneer_path ? String(f.pioneer_path).split('+').map((x: string) => x.trim()).filter(Boolean) : []
          const setParts = async (next: string[]) => {
            const path = next.length ? next.join(' + ') : null
            let col = f.owlrx_column
            let label = f.owlrx_label
            try {
              const maps = await api.listFieldMappingsFor(pharmacyId)
              let m = maps.items.find(x => x.owlrx_table === 'custom' && x.owlrx_column === col)
              if (!m && path) {
                // Not a PioneerRx column yet - adopt it, same derivation the source dropdown uses.
                const tail = next[0].split('.').pop()!.replace(/\[\d+\]/g, '')
                col = ('pio_' + tail.replace(/[^A-Za-z0-9]/g, '_')).toLowerCase().slice(0, 60)
                label = (f.owlrx_label || '').trim() || tail.replace(/([a-z])([A-Z])/g, '$1 $2')
                try { await api.createFieldMapping({ owlrx_table: 'custom', owlrx_column: col, owlrx_label: label, pioneer_path: path, card_key: 'list_columns' }, pharmacyId) }
                catch (e: any) { if (!String(e?.message ?? e).includes('mapping_exists')) throw e }
                m = (await api.listFieldMappingsFor(pharmacyId)).items.find(x => x.owlrx_table === 'custom' && x.owlrx_column === col)
              }
              if (m) await api.updateFieldMapping(m.id, { pioneer_path: path }, pharmacyId)
            } catch (e: any) { alert('Could not update the mapping: ' + (e?.message ?? e)); return }
            onMappingChanged?.()
            setFields(fs => fs ? fs.map((x, k) => k === i ? {
              ...x,
              owlrx_table: path ? 'custom' : x.owlrx_table,
              owlrx_column: col,
              owlrx_label: (x.owlrx_label || '').trim() ? x.owlrx_label : label,
              custom: path ? true : x.custom,
              pioneer_path: path,
              data_type: 'text',
            } : x) : fs)
          }
          return (
            <div key={`lcc-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', padding: '0 0 5px 98px', opacity: f.visible ? 1 : 0.45 }}>
              <span style={{ fontSize: 10.5, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>combine</span>
              {parts.map((pt, pi) => (
                <span key={pt} title={pt}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 600,
                           padding: '2px 5px 2px 7px', borderRadius: 999, border: '1px solid var(--color-border)',
                           background: 'var(--color-surface-2, #f8fafc)', color: 'var(--color-text)' }}>
                  {pt.split('.').pop()!.replace(/\[\d+\]/g, '')}
                  <button onClick={() => setParts(parts.filter((_, z) => z !== pi))}
                    title={`Remove ${pt}`} aria-label={`Remove ${pt}`}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, fontSize: 12, color: 'var(--color-text-muted)' }}>&times;</button>
                </span>
              ))}
              <SearchableSelect
                value=""
                ariaLabel={`Add a PioneerRx field to the ${(f.owlrx_label || f.owlrx_column)} column`}
                style={{ flex: '0 1 260px', minWidth: 170, fontSize: 11 }}
                options={[{ value: '', label: parts.length ? '— add another field —' : '— add a PioneerRx field —' },
                  ...(pioneerPaths ?? []).filter(pp => !parts.includes(pp.path)).map(pp => ({ value: pp.path, label: `PioneerRx: ${pp.path}` }))]}
                onChange={v => { if (v) void setParts([...parts, v]) }}
              />
              <span style={{ fontSize: 10.5, color: 'var(--color-text-muted)' }}>
                {parts.length > 1 ? `${parts.length} fields — rendered in order, separated by a space` : 'add another to join them with a space'}
              </span>
            </div>
          )
        })()}
      </>))}
      <button
        onClick={() => {
          // Pick the first catalog column NOT already in the panel — adding a duplicate of an
          // existing column violated UK_viewfield on save and rolled the whole save back.
          const present = new Set((fields ?? []).map(x => `${x.owlrx_table}.${x.owlrx_column}`))
          const first = catalog.find(c => !present.has(`${c.table}.${c.column}`)) ?? catalog[0]
          if (!first) return
          ls.setRemoved((r: Set<string>) => { const n = new Set(r); n.delete(`${first.table}.${first.column}`); return n })
          setFields(fs => [...(fs ?? []), { owlrx_table: first.table, owlrx_column: first.column, owlrx_label: first.label, pioneer_path: null, visible: true, custom: false, data_type: 'text' }])
        }}
        style={{ marginTop: 8, fontSize: 12, padding: '5px 10px', borderRadius: 7, border: '1px dashed var(--color-border)', background: 'transparent', color: 'var(--color-text)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
      ><Plus size={13} /> Add column</button>
      <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 10 }}>
        Pick its source from the dropdown{surface ? '' : ''} (any standard column or custom field). New mapped fields are created on detail cards and appear here automatically.
      </span>
    </div>
  )
}


// ---------------------------------------------------------------------------
// D-HOME-2 part B — metric card editor. Edits config.metric; the server-side
// allowlist (HomeMetricSql.Build) is the security boundary, this UI just keeps
// admins inside it. Saved with the layout like any other card config.
// ---------------------------------------------------------------------------
// ASP-53: pick which saved view a custom-view tile counts and links to.
function CustomViewTileEditor({ pharmacyId, config, onChange, onAutoTitle }: {
  pharmacyId: string
  config: Record<string, any> | null | undefined
  onChange: (patch: Record<string, any>) => void
  onAutoTitle: (title: string) => void
}) {
  const [views, setViews] = useState<api.PharmacyView[] | null>(null)
  const [err, setErr] = useState(false)
  // ASP-53b: SaaS-admin actors are unbound — the selected pharmacy must ride along
  // (the ASP-38 lesson; without it the endpoint 422s and the picker showed
  // "Couldn't load views").
  useEffect(() => { setErr(false); setViews(null); api.listViews(pharmacyId || undefined).then(r => setViews(r.items)).catch(() => setErr(true)) }, [pharmacyId])
  const sel = config?.view?.view_id ?? ''
  return (
    <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
      <span style={{ color: 'var(--color-text-soft)' }}>Saved view</span>
      {err ? <span style={{ color: '#b91c1c' }}>Couldn't load views.</span> : (
        <select
          value={sel}
          onChange={e => {
            const v = views?.find(x => x.id === e.target.value)
            onChange({ view: { view_id: e.target.value || undefined, view_name: v?.name } })
            if (v?.name) onAutoTitle(v.name)
          }}
          style={{ fontSize: 12, padding: '3px 6px', border: '1px solid var(--color-border)', borderRadius: 4 }}
        >
          <option value="">{views ? 'Pick a view…' : 'Loading…'}</option>
          {(views ?? []).map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
      )}
      <span style={{ color: 'var(--color-text-mute)' }}>Tile shows the view's live row count; clicking opens its worklist.</span>
    </div>
  )
}

function MetricEditor({ config, onChange }: { config: Record<string, any> | null | undefined; onChange: (patch: Record<string, any>) => void }) {
  const m = config?.metric ?? { source: 'rx_items', agg: 'count', filters: [], display: 'number' }
  const src = HOME_METRIC_CATALOG.sources[(m.source as 'rx_items') ?? 'rx_items'] ?? HOME_METRIC_CATALOG.sources.rx_items
  const set = (patch: Record<string, any>) => onChange({ metric: { ...m, ...patch } })
  const setFilter = (i: number, patch: Record<string, any>) => {
    const fs = [...(m.filters ?? [])]; fs[i] = { ...fs[i], ...patch }; set({ filters: fs })
  }
  const opsFor = (field: string) => (src.filterFields[field]?.kind === 'number' ? HOME_METRIC_CATALOG.numberOps : HOME_METRIC_CATALOG.textOps)
  const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', padding: '4px 10px', fontSize: 12 }
  const lbl: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-muted)', minWidth: 74 }
  return (
    <div style={{ padding: '4px 0 8px', borderTop: '1px solid var(--color-border-subtle, #f1f4f7)' }}>
      <div style={{ ...rowStyle, fontWeight: 600, color: 'var(--color-text-soft)' }}>{metricSummary({ metric: m })}</div>
      <div style={rowStyle}>
        <span style={lbl}>Source</span>
        <select value={m.source ?? 'rx_items'} onChange={e => set({ source: e.target.value, measure: undefined, group_by: undefined, filters: [] })} style={srcSelect}>
          {Object.entries(HOME_METRIC_CATALOG.sources).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
        </select>
        <span style={lbl}>Number</span>
        <select value={m.agg ?? 'count'} onChange={e => set({ agg: e.target.value, measure: e.target.value === 'count' ? undefined : (m.measure ?? src.measures[0]) })} style={srcSelect}>
          {HOME_METRIC_CATALOG.aggs.map(a => <option key={a} value={a}>{a.toUpperCase()}</option>)}
        </select>
        {m.agg && m.agg !== 'count' && (
          <select value={m.measure ?? ''} onChange={e => set({ measure: e.target.value })} style={srcSelect}>
            <option value="">— pick column —</option>
            {src.measures.map(c => <option key={c} value={c}>{src.filterFields[c]?.label ?? c}</option>)}
          </select>
        )}
      </div>
      <div style={rowStyle}>
        <span style={lbl}>Time window</span>
        <select value={m.window?.field ?? ''} onChange={e => set({ window: e.target.value ? { field: e.target.value, mode: m.window?.mode ?? 'within', unit: m.window?.unit ?? 'days', amount: m.window?.amount ?? m.window?.last_days ?? 14 } : undefined })} style={srcSelect}>
          <option value="">all time</option>
          {src.dateCols.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {m.window?.field && (
          <>
            <select value={m.window?.mode ?? 'within'} onChange={e => set({ window: { ...m.window, mode: e.target.value } })} style={srcSelect}>
              <option value="within">within last</option>
              <option value="older_than">older than</option>
            </select>
            <input type="number" min={1} max={m.window?.unit === 'hours' ? 8784 : 366} value={m.window?.amount ?? m.window?.last_days ?? 14}
              onChange={e => set({ window: { ...m.window, amount: Math.max(1, Number(e.target.value) || 1) } })}
              style={{ ...srcSelect, width: 72 }} />
            <select value={m.window?.unit ?? 'days'} onChange={e => set({ window: { ...m.window, unit: e.target.value } })} style={srcSelect}>
              <option value="days">days</option>
              <option value="hours">hours</option>
            </select>
          </>
        )}
        <span style={lbl}>Tiles per</span>
        <select value={m.group_by ?? ''} onChange={e => set({ group_by: e.target.value || undefined, display: e.target.value ? 'tiles' : 'number' })} style={srcSelect}>
          <option value="">no grouping (one number)</option>
          {src.groupBy.map(c => <option key={c} value={c}>{src.filterFields[c]?.label ?? c}</option>)}
        </select>
      </div>
      {(m.filters ?? []).map((f: any, i: number) => (
        <div key={i} style={rowStyle}>
          <span style={lbl}>{i === 0 ? 'Where' : 'and'}</span>
          <select value={f.field ?? ''} onChange={e => setFilter(i, { field: e.target.value, op: 'eq', values: [] })} style={srcSelect}>
            <option value="">— field —</option>
            {Object.entries(src.filterFields).map(([k, d]) => <option key={k} value={k}>{d.label}</option>)}
          </select>
          {f.field && (
            <select value={f.op ?? 'eq'} onChange={e => setFilter(i, { op: e.target.value })} style={srcSelect}>
              {opsFor(f.field).map(o => <option key={o} value={o}>{OP_LABELS[o]}</option>)}
            </select>
          )}
          {f.field && f.op !== 'is_null' && f.op !== 'not_null' && (
            <input value={(f.values ?? []).join(', ')} placeholder={f.op === 'in' ? 'value, value, …' : 'value'}
              onChange={e => setFilter(i, { values: e.target.value.split(',').map(s => s.trim()).filter(Boolean).slice(0, 20) })}
              style={{ ...srcSelect, width: 200 }} />
          )}
          <button onClick={() => set({ filters: (m.filters ?? []).filter((_: any, j: number) => j !== i) })} style={{ ...miniTextBtn, color: '#b3261e' }}>remove</button>
        </div>
      ))}
      <div style={rowStyle}>
        <button onClick={() => set({ filters: [...(m.filters ?? []), { field: '', op: 'eq', values: [] }] })} style={miniTextBtn}>+ filter</button>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Values are validated server-side against a fixed allowlist; only aggregate numbers are returned.</span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// D-HOME-2 part B — pulse tile control. Tiles stay data-driven (one per raw
// PioneerRx status); this edits display-layer config.pulse: hide / relabel /
// order. Statuses NOT listed here still render (appended after ordered ones),
// so new PioneerRx statuses never silently vanish.
// ---------------------------------------------------------------------------
function PulseTileEditor({ config, onChange }: { config: Record<string, any> | null | undefined; onChange: (patch: Record<string, any>) => void }) {
  const pulse = config?.pulse ?? { hidden: [], labels: {}, order: [] }
  const set = (patch: Record<string, any>) => onChange({ pulse: { ...pulse, ...patch } })
  const rows: string[] = [...new Set([...(pulse.order ?? []), ...KNOWN_PULSE_STATUSES, ...Object.keys(pulse.labels ?? {}), ...(pulse.hidden ?? [])])]
  const move = (s: string, dir: -1 | 1) => {
    const order = rows.filter(r => r !== '__new__'); const i = order.indexOf(s); const j = i + dir
    if (j < 0 || j >= order.length) return
    ;[order[i], order[j]] = [order[j], order[i]]; set({ order })
  }
  // Drag-to-reorder tiles (ASP pattern; arrows kept). '__new__' isn't draggable.
  const tileDrag = usePointerReorder((from, to) =>
    set({ order: reorderByKey(rows.filter(r => r !== '__new__'), x => x, from, to) }))
  const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '3px 10px', fontSize: 12 }
  return (
    <div style={{ padding: '4px 0 8px', borderTop: '1px solid var(--color-border-subtle, #f1f4f7)' }}>
      <div style={{ padding: '4px 10px', fontSize: 11.5, color: 'var(--color-text-muted)' }}>
        Tiles are data-driven — one per PioneerRx status in this pharmacy's data. Hide, relabel, or reorder them here; statuses not listed still appear.
      </div>
      {rows.map(s => {
        const hidden = (pulse.hidden ?? []).includes(s)
        return (
          <div key={s} {...(s !== '__new__' ? tileDrag.rowProps(s) : {})} style={{ ...rowStyle, opacity: hidden ? 0.45 : 1, outline: tileDrag.isDragging(s) ? '1.5px solid var(--color-accent, #0f766e)' : 'none' }}>
            {s !== '__new__' && <span {...tileDrag.handleProps(s)}><GripVertical size={11} style={{ color: 'var(--color-text-mute)' }} /></span>}
            <button onClick={() => move(s, -1)} style={miniTextBtn} aria-label={`Move ${s} up`}>↑</button>
            <button onClick={() => move(s, 1)} style={miniTextBtn} aria-label={`Move ${s} down`}>↓</button>
            <span style={{ minWidth: 170 }}>{s}</span>
            <input value={pulse.labels?.[s] ?? ''} placeholder="display label (optional)"
              onChange={e => set({ labels: { ...(pulse.labels ?? {}), [s]: e.target.value } })}
              style={{ ...srcSelect, width: 180 }} />
            <button onClick={() => set({ hidden: hidden ? (pulse.hidden ?? []).filter((x: string) => x !== s) : [...(pulse.hidden ?? []), s] })} style={miniTextBtn}>
              {hidden ? 'show' : 'hide'}
            </button>
          </div>
        )
      })}
      <div style={rowStyle}>
        <button onClick={() => { const s = prompt('Raw PioneerRx status to manage:'); if (s && !rows.includes(s)) set({ order: [...rows, s] }) }} style={miniTextBtn}>+ status</button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// D-HOME-3 — built-in card parameters. The four built-in action cards' filters
// were frozen in server code; they are now per-pharmacy params in config.params
// (validated + clamped server-side in HomeCardParams), and BOTH the count and
// the card's worklist follow. Absent params = historical defaults.
// ---------------------------------------------------------------------------
const BUILTIN_PARAM_DEFS: Record<string, { wiring: (p: Record<string, any>) => string; defaultTitle: (p: Record<string, any>) => string }> = {
  action_holds: {
    wiring: p => `COUNT of Rx where state = on_hold AND held ≥ ${p.hold_hours ?? 24} hours · worklist of the oldest`,
    defaultTitle: p => `Holds over ${p.hold_hours ?? 24}h`,
  },
  action_aging: {
    wiring: p => `COUNT of Rx where state in (draft, submitted) AND created more than ${p.aging_days ?? 10} days ago · worklist of the oldest`,
    defaultTitle: p => `Aging — pre-check over ${p.aging_days ?? 10} days`,
  },
  action_rejected: {
    // Review fix (2026-08-14): keep the builder honest with the card's ACTUAL predicate —
    // newest non-reversal claim carries NCPDP response status R, no time window
    // (RejectedClaimPredicate). The old text described the deleted state='rejected' + N-hours
    // rule; edits to rejected_hours saved successfully and changed nothing.
    wiring: () => 'COUNT of Rx whose newest claim was rejected (NCPDP status R) — open until rebilled',
    defaultTitle: () => 'Rejected claims',
  },
  action_prior_auth: {
    wiring: p => `COUNT of Rx where payer state in (${(p.pa_states ?? ['pa_required', 'pa_pending']).join(', ')})`,
    defaultTitle: () => 'Prior auth',
  },
  // ASP-74: per-pharmacy volume window (presets 30/90/120 or custom days).
  volume: {
    wiring: p => `Rx created per day over the last ${p.volume_days ?? 14} days · sparkline + daily average`,
    defaultTitle: p => `Volume — last ${p.volume_days ?? 14} days`,
  },
}
const ALL_PAYER_STATES = ['not_required', 'awaiting_insurance', 'awaiting_payer_response', 'pa_required', 'pa_pending', 'pa_approved', 'pa_denied', 'insurance_rejected', 'cash_pay']

function BuiltinParamsEditor({ cardKey, config, onChange }: { cardKey: string; config: Record<string, any> | null | undefined; onChange: (patch: Record<string, any>) => void }) {
  const def = BUILTIN_PARAM_DEFS[cardKey]
  const p = config?.params ?? {}
  const set = (patch: Record<string, any>) => onChange({ params: { ...p, ...patch } })
  const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '4px 10px', fontSize: 12 }
  const numInput = (key: string, fallback: number, max: number, unit: string) => (
    <>
      <input type="number" min={1} max={max} value={p[key] ?? fallback}
        onChange={e => set({ [key]: Math.max(1, Math.min(max, Number(e.target.value) || fallback)) })}
        style={{ ...srcSelect, width: 76 }} /> {unit}
    </>
  )
  return (
    <div style={{ padding: '4px 0 8px', borderTop: '1px solid var(--color-border-subtle, #f1f4f7)' }}>
      <div style={{ ...rowStyle, fontWeight: 600, color: 'var(--color-text-soft)' }}>{def.wiring(p)}</div>
      <div style={rowStyle}>
        {cardKey === 'action_holds' && <><span style={{ color: 'var(--color-text-muted)' }}>Count holds older than</span>{numInput('hold_hours', 24, 720, 'hours')}</>}
        {cardKey === 'action_aging' && <><span style={{ color: 'var(--color-text-muted)' }}>Count pre-check older than</span>{numInput('aging_days', 10, 365, 'days')}</>}
        {/* rejected_hours input removed (review fix 2026-08-14): the card no longer reads it —
            an open rejection does not stop being open by aging out of a window. The config
            FIELD stays in the schema untouched pending the Lucas/Maya ruling. */}
        {cardKey === 'volume' && (
          <>
            <span style={{ color: 'var(--color-text-muted)' }}>Window:</span>
            {[14, 30, 90, 120].map(d => {
              const on = (p.volume_days ?? 14) === d
              return (
                <button key={d} onClick={() => set({ volume_days: d })}
                  style={{ ...miniTextBtn, background: on ? 'var(--color-primary, #0d9488)' : 'transparent', color: on ? '#fff' : 'var(--color-text)', borderColor: on ? 'var(--color-primary, #0d9488)' : 'var(--color-border)' }}>
                  {d}d
                </button>
              )
            })}
            <span style={{ color: 'var(--color-text-muted)' }}>or custom</span>{numInput('volume_days', 14, 365, 'days')}
          </>
        )}
        {cardKey === 'action_prior_auth' && (
          <>
            <span style={{ color: 'var(--color-text-muted)' }}>Count payer states:</span>
            {ALL_PAYER_STATES.map(st => {
              const cur: string[] = p.pa_states ?? ['pa_required', 'pa_pending']
              const on = cur.includes(st)
              return (
                <button key={st} onClick={() => set({ pa_states: on ? cur.filter(x => x !== st) : [...cur, st] })}
                  style={{ ...miniTextBtn, background: on ? 'var(--color-primary, #0d9488)' : 'transparent', color: on ? '#fff' : 'var(--color-text)', borderColor: on ? 'var(--color-primary, #0d9488)' : 'var(--color-border)' }}>
                  {st}
                </button>
              )
            })}
          </>
        )}
      </div>
      <div style={{ ...rowStyle, color: 'var(--color-text-muted)', fontSize: 11 }}>
        Changing this updates the card's count AND its worklist on the home page after Save. Values are clamped and validated server-side.
      </div>
    </div>
  )
}
